/**
 * Accessible Snapshot Capture
 * 
 * Captures a clean, LLM-friendly representation of interactive elements
 * using browser-native accessibility patterns (no Playwright needed).
 * 
 * Key improvements over raw DOM snapshot:
 * - Only interactive elements (~90% reduction in noise)
 * - Computed accessible names (W3C accname algorithm)
 * - Semantic roles and states
 * - Context awareness (row context, section headings)
 * - Unique refs for easy element identification
 */

import { getSanitizedUrl } from './types';
import {
  getActiveModals,
  getActiveOverlays,
  isInsideActiveOverlay,
  isAllowedByModals,
  isAriaHidden,
  isInsideCollapsedDetails,
  isInsideCollapsedAccordion,
  checkInteractable as sharedCheckInteractable,
} from './visibility-utils';
import { logger as defaultLogger, Logger } from './logger';
import { collectDialogReadoutText } from './accessible-dialog-readout';

// ============================================================================
// Types
// ============================================================================

export interface AccessibleElement {
  /** Unique reference ID (e.g., "B1", "C2", "T3") */
  ref: string;
  
  /** Semantic role (button, link, textbox, card, etc.) */
  role: string;
  
  /** Computed accessible name */
  name: string;
  
  /** Element state (selected, checked, disabled, expanded) */
  state?: Record<string, boolean>;
  
  /** Current value for inputs */
  value?: string;
  
  /** Contextual info (section heading, row identifier) */
  context?: string;
  
  /** Row context for elements in tables/lists */
  rowContext?: string;
  
  /** Bounding rectangle for highlighting */
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  
  /** CSS selector for interaction (fallback) */
  selector?: string;
  
  /** Original tag name */
  tag?: string;

  /** Whether element can actually receive interaction (not disabled, not obscured) */
  interactable?: boolean;

  /**
   * If set, the element exists in the DOM but is not currently reachable.
   * The bot can use this to decide corrective action (e.g. close a modal,
   * expand an accordion) before targeting the element.
   */
  hiddenBy?: 'modal' | 'collapsed-details' | 'collapsed-accordion' | null;

  /** Whether this element currently has keyboard focus */
  focused?: boolean;

  /** Error message for invalid form fields */
  errorMessage?: string;

  /** Live region type — for toasts, alerts, notifications */
  liveRegion?: 'polite' | 'assertive';

  /**
   * Change status since last snapshot capture:
   * - 'new': element appeared since last snapshot
   * - 'changed': element's value or state changed since last snapshot
   */
  status?: 'new' | 'changed';

  /**
   * Scroll information for scrollable containers (listbox, menu, combobox panels).
   * Tells the LLM whether there's more content to scroll to.
   */
  scrollInfo?: {
    scrollable: boolean;
    hasMoreAbove: boolean;
    hasMoreBelow: boolean;
  };

  /** Stable test ID (data-testid, data-cy, data-qa, etc.) */
  testId?: string;

  /** Input constraints (required, min, max, maxlength, pattern, step, type, accept) */
  constraints?: Record<string, string>;

  /** Landmark region the element is in (nav, main, header, footer, etc.) */
  landmark?: string;

  /** Position in list/grid (e.g., "3 of 12") */
  positionInSet?: string;
}

export interface AccessibleSnapshot {
  /** Capture timestamp */
  timestamp: number;
  
  /** Page URL (without query params) */
  url: string;
  
  /** Page title */
  title: string;
  
  /** Viewport dimensions */
  viewport: {
    width: number;
    height: number;
  };
  
  /** List of accessible elements */
  elements: AccessibleElement[];
  
  /** Total element count */
  elementCount: number;
  
  /** Whether DOM changed since last snapshot */
  domChanged?: boolean;
  
  /** Reason for capture */
  captureReason?: string;

  /** Bounded visible, non-interactive text from the active dialog, if any. */
  readableText?: string;
}

// ============================================================================
// AccessibleSnapshotCapture Class
// ============================================================================

export class AccessibleSnapshotCapture {
  private readonly logger: Logger;

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
  }

  // Ref counters for generating unique IDs
  private refCounters: Record<string, number> = {};

  // Elements captured via the roleless cursor:pointer path (custom tabs/buttons
  // that carry no ARIA role — e.g. LeadRat drawer tabs `div.nav-item`). Tracked
  // per-capture so processElement can infer a tab/button role for them.
  private clickableDivs: WeakSet<HTMLElement> = new WeakSet();

  // Visible title-bearing wrappers around visually-hidden native checkboxes.
  // The wrapper is the exposed control; the native input remains unexposed.
  private proxyToggleCheckboxes: WeakMap<HTMLElement, HTMLInputElement> = new WeakMap();
  private proxyToggleActivationTargets: WeakMap<HTMLElement, HTMLLabelElement> = new WeakMap();
  private readOnlyProxyStates: string[] = [];

  // Cache for last snapshot (for change detection)
  private lastSnapshotHash: string = '';

  // Previous snapshot elements keyed by (role+name+selector) for diff detection
  private previousElementMap: Map<string, { value?: string; stateKey: string }> = new Map();
  
  // -------------------------------------------------------------------------
  // Static Configuration
  // -------------------------------------------------------------------------
  
  /** Implicit ARIA roles by HTML tag (W3C spec) */
  private static readonly IMPLICIT_ROLES: Record<string, string> = {
    'button': 'button',
    'a': 'link',
    'input': 'textbox',
    'textarea': 'textbox',
    'select': 'combobox',
    'option': 'option',
    'img': 'img',
    'nav': 'navigation',
    'main': 'main',
    'header': 'banner',
    'footer': 'contentinfo',
    'form': 'form',
    'table': 'table',
    'ul': 'list',
    'ol': 'list',
    'li': 'listitem',
    'article': 'article',
    'aside': 'complementary',
    'section': 'region',
    'dialog': 'dialog',
    'menu': 'menu',
    'menuitem': 'menuitem',
    'progress': 'progressbar',
    'meter': 'meter',
  };
  
  /** Input type to role mapping */
  private static readonly INPUT_TYPE_ROLES: Record<string, string> = {
    'button': 'button',
    'submit': 'button',
    'reset': 'button',
    'image': 'button',
    'checkbox': 'checkbox',
    'radio': 'radio',
    'range': 'slider',
    'search': 'searchbox',
    'email': 'textbox',
    'tel': 'textbox',
    'url': 'textbox',
    'password': 'textbox',
    'text': 'textbox',
    'number': 'spinbutton',
  };
  
  /** Role prefixes for ref generation */
  private static readonly ROLE_PREFIXES: Record<string, string> = {
    'button': 'B',
    'link': 'L',
    'textbox': 'T',
    'searchbox': 'T',
    'checkbox': 'X',
    'radio': 'R',
    'combobox': 'S',
    'listbox': 'S',
    'option': 'O',
    'tab': 'TAB',
    'menuitem': 'M',
    'menu': 'MENU',
    'img': 'I',
    'card': 'C',
    'dialog': 'D',
    'slider': 'SL',
    'spinbutton': 'N',
    'switch': 'SW',
    'navigation': 'NAV',
    'treeitem': 'TI',
    'gridcell': 'GC',
    'rowheader': 'RH',
    'columnheader': 'CH',
    'menuitemcheckbox': 'MX',
    'menuitemradio': 'MR',
    'alertdialog': 'AD',
    'progressbar': 'PB',
    'separator': 'SEP',
    'richtext': 'RT',
  };
  
  /** Interactive element selector */
  private static readonly INTERACTIVE_SELECTOR = `
    button,
    a[href],
    input:not([type="hidden"]),
    select,
    textarea,
    [role="button"],
    [role="link"],
    [role="tab"],
    [role="menuitem"],
    [role="menuitemcheckbox"],
    [role="menuitemradio"],
    [role="option"],
    [role="checkbox"],
    [role="radio"],
    [role="switch"],
    [role="combobox"],
    [role="listbox"],
    [role="slider"],
    [role="spinbutton"],
    [role="searchbox"],
    [role="textbox"],
    [role="treeitem"],
    [role="gridcell"],
    [role="rowheader"],
    [role="columnheader"],
    [role="alertdialog"],
    [role="progressbar"],
    [role="separator"][tabindex],
    [tabindex]:not([tabindex="-1"]),
    [contenteditable="true"],
    summary
  `.replace(/\s+/g, ' ').trim();
  
  /** Selectors to skip (our own UI, scripts, etc.) */
  private static readonly SKIP_SELECTORS = [
    // Floe's own UI
    '#onboarding-overlay',
    '#onboarding-sdk-react-ui',
    '#hse-highlight-element',
    '[data-floe-widget]',
    'script',
    'style',
    'noscript',
    'template',
    // Third-party chat/support widgets — never target these
    '#intercom-container',
    '#intercom-frame',
    'iframe[name="intercom-launcher-frame"]',
    'iframe[name="intercom-messenger-frame"]',
    '[class*="intercom"]',
    '#drift-widget',
    '#drift-frame',
    '#hubspot-messages-iframe-container',
    '[class*="drift-"]',
    '#crisp-chatbox',
    '[class*="crisp-"]',
    '#tawk-bubble-container',
    '#tawk-chat-widget',
    '[class*="tawk-"]',
    '#freshworks-container',
    '#fc_frame',
    '[class*="freshworks"]',
    '#zsiq_float',
    '[class*="zopim"]',
    '#launcher',  // Generic launcher ID used by many chat widgets
    '[data-testid="chat-widget"]',
  ];

  /**
   * Containers for custom / read-only select controls whose current selection is
   * rendered as TEXT rather than held in a native <select> or <input>. Angular
   * ng-select (LeadRat General Settings' Default Unit / Country Code / TimeZone /
   * Date Format) is the motivating case: the exposed element is an empty
   * read-only search input, so without a display read the prompt shows the
   * control's label and no value at all.
   *
   * `role="combobox"` / `role="listbox"` are here for the wrapper pattern
   * (React Select, Ant Design), NOT so the exposed element can be its own
   * container — see resolveCustomSelectContainer().
   */
  private static readonly CUSTOM_SELECT_CONTAINER_SELECTOR = [
    'ng-select',
    '.ng-select',
    '.ng-select-container',
    'nz-select',
    '.ant-select',
    'mat-select',
    '[class*="react-select"]',
    '[class*="select-container"]',
    '[class*="Select"]',
    '[role="combobox"]',
    '[role="listbox"]',
  ].join(', ');

  /** Rendered selected-value nodes inside a custom select container. */
  private static readonly CUSTOM_SELECT_VALUE_SELECTOR = [
    '.ng-value-label',
    '.ng-value',
    'nz-select-item',
    '.nz-select-item-label',
    '.ant-select-selection-item',
    '[class*="singleValue"]',
    '[class*="SingleValue"]',
    '[class*="select-value"]',
    '[class*="selected-value"]',
  ].join(', ');

  /**
   * Placeholder nodes. A placeholder is the ABSENCE of a selection, so a
   * placeholder-only control must report no value — never "Select"/"Choose".
   */
  private static readonly CUSTOM_SELECT_PLACEHOLDER_SELECTOR = [
    '.ng-placeholder',
    'nz-select-placeholder',
    '.ant-select-selection-placeholder',
    '[class*="placeholder"]',
    '[class*="Placeholder"]',
  ].join(', ');

  /**
   * A nested control inside a select container owns its own display text. The
   * walk up from a candidate node stops at the container, so the container's
   * own attributes never disqualify it; only controls in between do.
   */
  private static readonly NESTED_CONTROL_SELECTOR = [
    'ng-select',
    'nz-select',
    'mat-select',
    'input',
    'select',
    'textarea',
    'button',
    '[role="combobox"]',
    '[role="listbox"]',
    '[contenteditable="true"]',
  ].join(', ');
  
  // -------------------------------------------------------------------------
  // Main Capture Method
  // -------------------------------------------------------------------------
  
  /**
   * Capture accessible snapshot of the current page
   */
  capture(captureReason?: string): AccessibleSnapshot {
    this.refCounters = {}; // Reset ref counters
    this.clickableDivs = new WeakSet(); // Reset per-capture roleless-clickable set
    this.proxyToggleCheckboxes = new WeakMap();
    this.proxyToggleActivationTargets = new WeakMap();
    this.readOnlyProxyStates = [];
    // NOTE (perf, load-bearing): every capture path below is READ-ONLY — no
    // `.style`/`classList`/DOM writes during a capture pass. This keeps style &
    // layout clean so the many getComputedStyle/getBoundingClientRect reads are
    // served from one amortized recalc rather than thrashing. Do NOT introduce a
    // DOM/style write mid-capture; it re-dirties layout and turns every read into
    // a fresh reflow across all candidates.

    const elements: AccessibleElement[] = [];
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    let readableText = collectDialogReadoutText(
      el => this.shouldIncludeElement(el) && this.getBlockedReason(el) === null,
    );
    
    try {
      // 1. Find visible proxy toggle wrappers before collecting native controls.
      // Their visually-hidden checkbox stays out of the prompt to avoid duplicates.
      const proxyToggleElements = this.queryProxyToggleElements();
      if (this.readOnlyProxyStates.length) {
        readableText = [readableText, 'Visible control states (read only):', ...this.readOnlyProxyStates].filter(Boolean).join('\n');
      }

      // 2. Get all explicitly interactive elements
      const interactiveElements = this.queryInteractiveElements();

      // 3. Get elements with cursor:pointer (custom interactive components)
      const cursorPointerElements = this.queryCursorPointerElements();

      // 4. Get elements from active overlays (dropdowns, menus, popovers)
      const overlayElements = this.queryOverlayElements();

      // 5. Get elements from same-origin iframes
      const iframeElements = this.queryIframeElements();

      // 6. Combine and dedupe
      const allElements = this.dedupeElements([
        ...interactiveElements,
        ...proxyToggleElements,
        ...cursorPointerElements,
        ...overlayElements,
        ...iframeElements,
      ].filter(el => !this.isProxyToggleCheckbox(el) && !this.isReadOnlyProxyElement(el)));
      
      // 7. Process each element (with error handling per element)
      // Keep each result paired with its source node: step 6b needs to walk the
      // DOM again for duplicate-named elements, and the pairing must survive the
      // sort in step 7 (so an index-parallel array would not do).
      const sourceNodes = new Map<AccessibleElement, HTMLElement>();
      for (const el of allElements) {
        try {
          const elementInfo = this.processElement(el, viewport);
          if (elementInfo) {
            elements.push(elementInfo);
            sourceNodes.set(elementInfo, el);
          }
        } catch (e) {
          // Skip elements that cause errors
          this.logger.warn('[AccessibleSnapshot] Error processing element:', e);
        }
      }

      // 7b. Give duplicate-named elements their owning component's label.
      this.addOwnerContextForDuplicates(elements, sourceNodes);

      // 8. Sort by visual position (top to bottom, left to right)
      elements.sort((a, b) => {
        const yDiff = a.rect.y - b.rect.y;
        if (Math.abs(yDiff) < 20) {
          return a.rect.x - b.rect.x;
        }
        return yDiff;
      });

      // 9. Re-number refs after sorting
      this.renumberRefs(elements);

      // 10. Mark focused element
      const activeEl = document.activeElement;
      if (activeEl && activeEl !== document.body) {
        for (const elem of elements) {
          // Match by selector since we can't hold DOM references after processing
          if (elem.selector) {
            try {
              const matched = document.querySelector(elem.selector);
              if (matched === activeEl || activeEl.closest(elem.selector) === matched) {
                elem.focused = true;
                break;
              }
            } catch { /* invalid selector */ }
          }
        }
      }

      // 11. Compute diff against previous snapshot
      const currentElementMap = new Map<string, { value?: string; stateKey: string }>();
      for (const elem of elements) {
        const key = `${elem.role}|${elem.name}|${elem.selector || ''}`;
        const stateKey = elem.state ? Object.entries(elem.state).sort().map(([k, v]) => `${k}:${v}`).join(',') : '';
        currentElementMap.set(key, { value: elem.value, stateKey });

        const prev = this.previousElementMap.get(key);
        if (!prev) {
          elem.status = 'new';
        } else if (prev.value !== elem.value || prev.stateKey !== stateKey) {
          elem.status = 'changed';
        }
        // else: unchanged, no status field
      }
      this.previousElementMap = currentElementMap;

    } catch (e) {
      this.logger.error('[AccessibleSnapshot] Error during capture:', e);
    }

    // 12. Detect if DOM changed
    const currentHash = this.computeSnapshotHash(elements, readableText);
    const domChanged = currentHash !== this.lastSnapshotHash;
    this.lastSnapshotHash = currentHash;

    return {
      timestamp: Date.now(),
      url: getSanitizedUrl(),
      title: document.title,
      viewport,
      elements,
      elementCount: elements.length,
      domChanged,
      captureReason,
      ...(readableText ? { readableText } : {}),
    };
  }
  
  /**
   * Capture with stability check (waits for DOM to settle)
   */
  async captureWhenStable(timeout = 2000, captureReason?: string): Promise<AccessibleSnapshot> {
    const startTime = Date.now();
    let lastSnapshot = this.capture(captureReason);
    
    while (Date.now() - startTime < timeout) {
      await this.sleep(200);
      const newSnapshot = this.capture(captureReason);
      
      if (this.snapshotsAreEqual(lastSnapshot, newSnapshot)) {
        return newSnapshot;
      }
      lastSnapshot = newSnapshot;
    }
    
    return lastSnapshot;
  }
  
  // -------------------------------------------------------------------------
  // Element Querying
  // -------------------------------------------------------------------------
  
  private queryInteractiveElements(): HTMLElement[] {
    const elements = Array.from(
      document.querySelectorAll(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)
    ) as HTMLElement[];

    // Also query inside open shadow roots (Web Components)
    this.queryShadowRoots(document.body, elements);

    // Also capture live regions (toasts, notifications, alerts) for LLM context
    const liveRegionSelector = [
      '[role="alert"]',
      '[role="status"]',
      '[aria-live="polite"]',
      '[aria-live="assertive"]',
      '[class*="toast"]:not([aria-hidden="true"])',
      '[class*="Toast"]:not([aria-hidden="true"])',
      '[class*="notification"]:not([aria-hidden="true"])',
      '[class*="Notification"]:not([aria-hidden="true"])',
      '[class*="snackbar"]:not([aria-hidden="true"])',
      '[class*="Snackbar"]:not([aria-hidden="true"])',
    ].join(', ');
    try {
      const liveRegions = Array.from(document.querySelectorAll(liveRegionSelector)) as HTMLElement[];
      for (const lr of liveRegions) {
        const text = (lr.textContent || '').trim();
        // Only include live regions that have actual content and aren't already in the list
        if (text && text.length > 0 && !elements.includes(lr)) {
          elements.push(lr);
        }
      }
    } catch { /* invalid selector */ }

    return elements.filter(el => this.shouldIncludeElement(el));
  }

  /**
   * Surface a visible title-bearing proxy as a switch when it owns exactly one
   * direct checkbox and one direct visible label. Many CSS switches make their
   * native checkbox transparent while the labelled wrapper is what users see.
   * This never makes the hidden input itself targetable or readable.
   */
  private queryProxyToggleElements(): HTMLElement[] {
    const hosts: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();
    for (const checkbox of Array.from(document.querySelectorAll('input[type="checkbox"]'))) {
      if (!(checkbox instanceof HTMLInputElement)) continue;
      const host = checkbox.parentElement;
      if (!host || seen.has(host)) continue;
      const title = host.getAttribute('title')?.trim();
      if (!title || title.length > 100 || !this.shouldIncludeElement(host)) continue;
      const checkboxRect = checkbox.getBoundingClientRect();
      if (this.isVisible(checkbox) && checkboxRect.width > 0 && checkboxRect.height > 0 &&
          checkboxRect.right > 0 && checkboxRect.bottom > 0 &&
          checkboxRect.left < window.innerWidth && checkboxRect.top < window.innerHeight) continue;
      const directCheckboxes = Array.from(host.children).filter(
        child => child instanceof HTMLInputElement && child.type === 'checkbox',
      ) as HTMLInputElement[];
      if (directCheckboxes.length !== 1 || directCheckboxes[0] !== checkbox) continue;
      const visibleLabels = Array.from(host.children).filter(child => {
        if (!(child instanceof HTMLLabelElement) ||
            child.closest('[aria-hidden="true"]') || !this.isVisible(child) ||
            this.getBlockedReason(child) !== null) return false;
        const rect = child.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      if (!visibleLabels.length) continue;
      seen.add(host);
      this.proxyToggleCheckboxes.set(host, checkbox);
      const activationLabel = visibleLabels.find(child => child instanceof HTMLLabelElement && child.control === checkbox && !checkbox.disabled);
      if (!(activationLabel instanceof HTMLLabelElement)) {
        // CSS-only labels do not prove an activation target. Expose the native
        // state as bounded read-only evidence, without manufacturing a click ref.
        if (this.readOnlyProxyStates.length < 40) {
          this.readOnlyProxyStates.push(JSON.stringify({
            name: title, context: this.getProxyToggleRowContext(host), checked: checkbox.checked,
          }));
        }
        continue;
      }
      this.proxyToggleActivationTargets.set(host, activationLabel);
      hosts.push(host);
    }
    return hosts;
  }

  private isProxyToggleCheckbox(el: HTMLElement): boolean {
    return el instanceof HTMLInputElement && !!el.parentElement &&
      this.proxyToggleCheckboxes.get(el.parentElement) === el;
  }

  private isReadOnlyProxyElement(el: HTMLElement): boolean {
    const host = this.proxyToggleCheckboxes.has(el) ? el : el.parentElement;
    if (!host || !this.proxyToggleCheckboxes.has(host) || this.proxyToggleActivationTargets.has(host)) return false;
    // A CSS-only proxy contributes its title and state as read-only evidence.
    // Hide that host, its one hidden checkbox, and its visual label, but leave
    // unrelated direct siblings (including labels for other controls) targetable.
    const checkbox = this.proxyToggleCheckboxes.get(host);
    return el === host || checkbox === el ||
      (el instanceof HTMLLabelElement && el.parentElement === host &&
        (!el.control || el.control === checkbox));
  }

  /**
   * Recursively query interactive elements inside open shadow roots.
   * Web Components (Salesforce Lightning, Shopify Polaris, etc.) render
   * content in shadow DOM that's invisible to document.querySelectorAll.
   */
  private queryShadowRoots(root: Element | DocumentFragment, results: HTMLElement[], depth: number = 0): void {
    if (depth > 5) return; // Prevent infinite recursion

    const children = root instanceof DocumentFragment
      ? Array.from(root.children)
      : Array.from(root.querySelectorAll('*'));

    // Limit scan to first 500 elements per level for performance
    const limit = Math.min(children.length, 500);
    for (let i = 0; i < limit; i++) {
      const el = children[i];
      const shadowRoot = el.shadowRoot;
      if (shadowRoot) {
        // Query interactive elements inside this shadow root
        try {
          const shadowElements = Array.from(
            shadowRoot.querySelectorAll(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)
          ) as HTMLElement[];
          results.push(...shadowElements);
        } catch { /* skip */ }

        // Recurse into nested shadow roots
        this.queryShadowRoots(shadowRoot, results, depth + 1);
      }
    }
  }

  /**
   * Query interactive elements inside active overlay containers (dropdowns,
   * menus, popovers). These are often rendered via portals outside the main
   * DOM tree and may be missed by the standard query + visibility filters.
   */
  private queryOverlayElements(): HTMLElement[] {
    const overlays = getActiveOverlays();
    if (overlays.length === 0) return [];

    const elements: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();

    // Selectors for interactive items typically found in overlays
    const overlayInteractiveSelector = [
      '[role="option"]',
      '[role="menuitem"]',
      '[role="menuitemcheckbox"]',
      '[role="menuitemradio"]',
      '[role="treeitem"]',
      '[role="listbox"]',
      '[role="menu"]',
      '[role="button"]',
      '[role="link"]',
      '[role="checkbox"]',
      '[role="radio"]',
      '[role="switch"]',
      '[role="tab"]',
      '[role="combobox"]',
      '[role="searchbox"]',
      '[role="textbox"]',
      'button',
      'a[href]',
      'input:not([type="hidden"])',
      'select',
      'textarea',
      'li',
      // Framework-specific option elements
      'nz-option-item',
      '.ant-select-item-option',
      '.ant-select-item',
      '.ant-dropdown-menu-item',
      '.ant-cascader-menu-item',
      '[class*="option"]:not([class*="singleValue"]):not([class*="placeholder"])',
      '[class*="Option"]:not([class*="singleValue"]):not([class*="placeholder"])',
      '[class*="menu-item"]',
      '[class*="MenuItem"]',
    ].join(', ');

    for (const overlay of overlays) {
      try {
        const items = Array.from(overlay.querySelectorAll(overlayInteractiveSelector));
        for (const item of items) {
          const el = item as HTMLElement;
          if (seen.has(el)) continue;
          seen.add(el);

          // Skip elements with no visual size
          const rect = el.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) continue;

          // Skip hidden elements
          try {
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') continue;
          } catch { continue; }

          // Skip our own UI
          let skipOwn = false;
          for (const selector of AccessibleSnapshotCapture.SKIP_SELECTORS) {
            if (el.matches(selector) || el.closest(selector)) {
              skipOwn = true;
              break;
            }
          }
          if (skipOwn) continue;

          elements.push(el);
        }
      } catch {
        // Invalid selector or overlay detached, skip
      }
    }

    this.logger.log(`[AccessibleSnapshot] Found ${elements.length} overlay elements from ${overlays.length} active overlays`);
    return elements;
  }
  
  /**
   * Query interactive elements inside same-origin iframes.
   * Cross-origin iframes are inaccessible from JS — we skip those.
   */
  private queryIframeElements(): HTMLElement[] {
    const elements: HTMLElement[] = [];
    try {
      const iframes = Array.from(document.querySelectorAll('iframe')) as HTMLIFrameElement[];
      for (const iframe of iframes) {
        try {
          const doc = iframe.contentDocument;
          if (!doc) continue; // Cross-origin or not loaded
          const iframeElements = Array.from(
            doc.querySelectorAll(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)
          ) as HTMLElement[];
          elements.push(...iframeElements);
        } catch {
          // Cross-origin iframe — contentDocument access throws SecurityError
        }
      }
    } catch { /* skip */ }
    return elements;
  }

  private queryCursorPointerElements(): HTMLElement[] {
    // More targeted query for potential interactive containers
    // Focus on elements that commonly have click handlers
    // Class-token candidate patterns. All component-name tokens use the CSS4
    // case-insensitive flag [class*="x" i] so they match lowercase (LeadRat/Ant/
    // Tailwind) AND PascalCase CSS-module classes (React apps like Rocketlane emit
    // `components__TabWrapper-hash`, `NavItem`, `Btn`). Previously each token was
    // duplicated per-case (card/Card) and STILL missed PascalCase, so hashed-class
    // apps depended entirely on ARIA role / tabindex to be captured. The
    // cursor:pointer gate + size/leaf/contains-interactive guards + MAX caps
    // downstream decide what is actually kept, so broad tokens cannot flood the tree.
    const targetedSelectors = [
      // Common clickable container patterns
      '[class*="card" i]:not(button):not(a)',
      '[class*="tile" i]:not(button):not(a)',
      '[class*="option" i]:not(button):not(a)',
      '[class*="chip" i]:not(button):not(a)',
      '[class*="clickable" i]',
      '[class*="selectable" i]',
      '[class*="toggle" i]:not(button):not(a):not(input)',
      '[class*="segment" i]:not(button):not(a)',
      // Interactive list / menu items (Ant `ant-menu-item`, MUI `MenuItem`, `ListItem`)
      'li[class*="item" i]',
      '[class*="menuitem" i]:not(button):not(a)',
      '[class*="menu-item" i]:not(button):not(a)',
      '[class*="listitem" i]:not(button):not(a)',
      // Explicit cursor styling in class
      '[class*="cursor-pointer" i]',
      '[class*="pointer" i]',
      // Compact table-row actions can be roleless icon divs whose only name is
      // their tooltip title (LeadRat: Download/Delete/Assign To/Config). The exact
      // class token + non-empty title is narrow enough to avoid ordinary status
      // badges while making the row action and its rowContext available by ref.
      '[class~="icon-badge"][title]:not(button):not(a):not(input)',
      // Click-related data attributes
      '[data-clickable]',
      '[data-action]',
      '[data-onclick]',
      // Roleless custom tabs/buttons that carry NO ARIA role and so are missed by
      // the semantic INTERACTIVE_SELECTOR path — verified against LeadRat's drawer
      // (`div.nav-item` tabs, `div.btn` "Change Status"). Both hyphenated and
      // CSS-module (NavItem/TabItem/Btn) forms. Deliberately NOT bare
      // [role=presentation]/[role=none]: those scoop ag-Grid mirror/guard internals.
      '[class*="nav-item" i]:not(button):not(a):not(input)',
      '[class*="navitem" i]:not(button):not(a):not(input)',
      '[class*="tab-item" i]:not(button):not(a):not(input)',
      '[class*="tabitem" i]:not(button):not(a):not(input)',
      '[class*="btn" i]:not(button):not(a):not(input)',
      // Grid view-filter chips (LeadRat leads toolbar: All/My Leads/Team's/Deleted/…).
      // 'nav-bar-item' is NOT a substring of 'nav-item', so the token above misses
      // them. Use the whitespace-token form [class~=] (not [class*=]) so it matches
      // the exact class token and NOT 'nav-bar-item-container'/'nav-bar-items' wrappers
      // on other prospect sites (which would flood a strip-level duplicate).
      '[class~="nav-bar-item"]:not(button):not(a):not(input)',
    ].join(', ');

    const candidates: HTMLElement[] = [];

    try {
      const targetedElements = Array.from(
        document.querySelectorAll(targetedSelectors)
      ) as HTMLElement[];
      candidates.push(...targetedElements);
    } catch {
      // Invalid selector, continue without targeted elements
    }

    // Also check direct children of main containers (limited scope)
    const mainContainers = document.querySelectorAll('main, [role="main"], #app, #root, .app, .main');
    for (const container of mainContainers) {
      const children = Array.from(container.querySelectorAll(':scope > div, :scope > section > div')) as HTMLElement[];
      candidates.push(...children.slice(0, 50)); // Limit to avoid performance issues
    }

    // Prioritise candidates inside an active dialog/overlay so the MAX cap below
    // can never starve a drawer's tab bar (often late in DOM order) behind
    // earlier page-level cards/buttons — the exact silent-miss the cap risks.
    const inDialogScope = (el: HTMLElement): boolean => {
      try { return !!el.closest('[role="dialog"],[role="alertdialog"],[aria-modal="true"],dialog'); }
      catch { return false; }
    };
    candidates.sort((a, b) => (inDialogScope(b) ? 1 : 0) - (inDialogScope(a) ? 1 : 0));

    // Bound worst-case getComputedStyle cost: cap candidates EXAMINED (not just
    // kept), since on class-heavy apps many candidates fail the cursor check and
    // the kept-cap alone wouldn't fire. And cap KEPT output to bound the LLM tree.
    //
    // MAX_CLICKABLE was 40, which silently truncated real card grids: LeadRat's
    // integration page has 16 roleless "Connect Now" cards and the 16th fell off
    // the end, making it unreachable for the bot with no trace. The prompt
    // builder already caps at 200 and SAYS how many it dropped; this cap must be
    // generous enough that it is not the one deciding, and must say so when it
    // does bite.
    const MAX_EXAMINED = 400;
    const MAX_CLICKABLE = 120;
    const examined = candidates.slice(0, MAX_EXAMINED);
    if (candidates.length > MAX_EXAMINED) {
      // Reported independently of the MAX_CLICKABLE warning below. That one only
      // fires when the KEPT cap bites, so a page with 400+ candidates whose
      // filters reject most of them dropped everything past 400 with no trace at
      // all — the silent truncation this block's own comment demands we announce.
      this.logger.warn(
        `[AccessibleSnapshot] Examined cap hit (${MAX_EXAMINED}) — ` +
        `${candidates.length - MAX_EXAMINED} candidate(s) never looked at. ` +
        `Some controls are invisible to the agent on this page.`
      );
    }

    // Dedupe and filter
    const seen = new Set<HTMLElement>();
    const kept: HTMLElement[] = [];
    // Index of the candidate the cap stopped us at, so the warning can report
    // what was never looked at. examined.length - kept.length would also count
    // everything the filters below rejected, which WAS examined.
    let stoppedAt = -1;
    for (let i = 0; i < examined.length; i++) {
      const el = examined[i];
      if (kept.length >= MAX_CLICKABLE) { stoppedAt = i; break; }
      if (seen.has(el)) continue;
      seen.add(el);

      if (!this.shouldIncludeElement(el)) continue;

      // Belt-and-suspenders: never treat ag-Grid structural internals as clickable
      // targets — positional resolution to their invisible mirror copies is the
      // exact bug the ag-Grid anchoring work fixed.
      const cls = typeof el.className === 'string' ? el.className : '';
      if (/\bag-/.test(cls)) continue;

      // Check cursor style. Titled icon-badge actions are an explicit semantic
      // carve-out: some component libraries attach the click handler without also
      // setting cursor:pointer, but the exact action class + tooltip still identifies
      // a compact control rather than a decorative badge.
      const style = window.getComputedStyle(el);
      const isTitledIconAction =
        el.classList.contains('icon-badge') &&
        !!el.getAttribute('title')?.trim();
      if (style.cursor !== 'pointer' && !isTitledIconAction) continue;

      // Reject giant containers: a full-bleed cursor:pointer wrapper is not a
      // discrete control (looksLikeCard only enforces a MIN size — this is the
      // missing MAX).
      const rect = el.getBoundingClientRect();
      if (rect.width > window.innerWidth * 0.9 && rect.height > window.innerHeight * 0.5) continue;

      // Reject wrapper divs: real tabs/buttons are small labelled leaves, not
      // many-child containers.
      if (el.children.length > 12 && !this.hasDirectText(el)) continue;

      // Skip label/wrapper elements that CONTAIN a semantic interactive descendant:
      // the real control is already captured on the INTERACTIVE_SELECTOR path, so
      // capturing the wrapper here produces a duplicate. This is the fix for the
      // Bootstrap `<div class="btn"><input type="radio"></div>` double-capture —
      // the wrapper was landing as a 2nd 'button' entry (via inferClickableRole,
      // which runs after dedupe so dedupe can't collapse it) for a control already
      // captured as 'radio'. Leaf chips/tabs (no interactive descendant) are kept.
      if (el.querySelector(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)) continue;

      // Skip if it's just inheriting from parent — unless it looks like a
      // distinct card/tile (cloud provider tiles, option cards, etc. often
      // sit inside a cursor:pointer container but are individual targets).
      const parent = el.parentElement;
      if (parent) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.cursor === 'pointer') {
          if (!el.onclick && !el.getAttribute('onclick') && !el.getAttribute('role')) {
            if (!this.looksLikeCard(el)) {
              continue;
            }
          }
        }
      }

      this.clickableDivs.add(el);
      kept.push(el);
    }
    if (stoppedAt >= 0) {
      this.logger.warn(
        `[AccessibleSnapshot] Roleless-clickable cap hit (${MAX_CLICKABLE}) — ` +
        `${examined.length - stoppedAt} candidate(s) not examined. Some controls ` +
        `are invisible to the agent on this page.`
      );
    }
    return kept;
  }

  /** True if the element has at least one non-whitespace direct text node child. */
  private hasDirectText(el: HTMLElement): boolean {
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === 3 && (node.textContent || '').trim()) return true;
    }
    return false;
  }
  
  private shouldIncludeElement(el: HTMLElement): boolean {
    // Skip our own UI elements
    for (const selector of AccessibleSnapshotCapture.SKIP_SELECTORS) {
      if (el.matches(selector) || el.closest(selector)) {
        return false;
      }
    }

    // Elements inside active overlays (dropdowns, menus, popovers) are always
    // included — they're rendered in portals and may be aria-hidden or outside
    // the viewport from the main tree's perspective but are actually visible.
    const inOverlay = isInsideActiveOverlay(el);
    if (inOverlay) {
      // Still skip truly hidden overlay elements
      try {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      } catch { /* allow through */ }
      return true;
    }

    // Skip elements with aria-hidden="true" (decorative/hidden from assistive tech).
    // EXCEPTION: When a modal is active, the page content behind it is often
    // marked aria-hidden="true" by the modal's focus trap. Those elements should
    // still be included (they'll be annotated with hiddenBy:'modal' in processElement).
    const isAriaHiddenEl = el.getAttribute('aria-hidden') === 'true' || !!el.closest('[aria-hidden="true"]');
    if (isAriaHiddenEl) {
      const modals = getActiveModals();
      const isHiddenByModal = modals.length > 0 && !modals.some(m => m.contains(el));
      if (!isHiddenByModal) {
        // No active modal, or element is inside a modal — genuinely decorative, skip
        return false;
      }
      // Otherwise: hidden by modal focus trap — include for annotation
    }
    
    // Skip elements with presentation/none role (decorative)
    const role = el.getAttribute('role');
    if (role === 'presentation' || role === 'none') {
      return false;
    }
    
    // Skip invisible elements — but allow elements inside collapsed <details>
    // and collapsed accordions through so they can be annotated with hiddenBy.
    if (!this.isVisible(el)) {
      // Check if the invisibility is due to a collapsed container
      const inClosedDetails = isInsideCollapsedDetails(el);
      const inClosedAccordion = isInsideCollapsedAccordion(el, 8);
      if (!inClosedDetails && !inClosedAccordion) {
        return false;
      }
      // Element is hidden by a collapsed container — let it through for annotation
    }
    
    // Skip elements outside viewport or with no size — but allow elements
    // that are hidden due to collapsed containers (they'll be annotated).
    const rect = el.getBoundingClientRect();
    const hasNoVisualPresence = (
      rect.bottom < 0 || rect.top > window.innerHeight ||
      rect.right < 0 || rect.left > window.innerWidth ||
      rect.width <= 0 || rect.height <= 0
    );
    if (hasNoVisualPresence) {
      const inClosedDetails = isInsideCollapsedDetails(el);
      const inClosedAccordion = isInsideCollapsedAccordion(el, 8);
      if (!inClosedDetails && !inClosedAccordion) {
        return false;
      }
    }

    // NOTE: Modal, collapsed-details, and collapsed-accordion checks are NOT
    // performed here. Instead, those elements are INCLUDED but annotated with
    // a `hiddenBy` reason in processElement(). This lets the LLM know the
    // element exists and what it needs to do to reach it (close modal, expand
    // accordion, etc.). The coordinate-based dom-capture.ts layer still filters
    // these out since the bot can't click them by position.
    
    return true;
  }
  
  private isVisible(el: HTMLElement): boolean {
    // Quick check using offsetParent (returns null for hidden elements)
    // Note: This doesn't work for position:fixed elements, so we check that too
    if (el.offsetParent === null) {
      const style = window.getComputedStyle(el);
      // Fixed position elements have null offsetParent but may be visible
      if (style.position !== 'fixed') {
        // Check if it's hidden or just has no positioned ancestor
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }
      }
    }
    
    // Full visibility check
    try {
      const style = window.getComputedStyle(el);
      
      if (style.display === 'none') return false;
      if (style.visibility === 'hidden') return false;
      if (style.opacity === '0') return false;
      
      // Check ancestors (limit depth for performance)
      let parent = el.parentElement;
      let depth = 0;
      const maxDepth = 10; // Limit ancestor checking for performance
      
      while (parent && parent !== document.body && depth < maxDepth) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
          return false;
        }
        parent = parent.parentElement;
        depth++;
      }
      
      return true;
    } catch (e) {
      // If getComputedStyle fails, assume visible
      return true;
    }
  }
  
  private dedupeElements(elements: HTMLElement[]): HTMLElement[] {
    const seen = new Set<HTMLElement>();
    const result: HTMLElement[] = [];
    
    // Cache computed values for performance (avoid repeated DOM walks)
    const depthCache = new Map<HTMLElement, number>();
    const roleCache = new Map<HTMLElement, string>();
    const nameCache = new Map<HTMLElement, string>();
    
    const getDepth = (el: HTMLElement): number => {
      if (!depthCache.has(el)) {
        depthCache.set(el, this.getElementDepth(el));
      }
      return depthCache.get(el)!;
    };
    
    const getRole = (el: HTMLElement): string => {
      if (!roleCache.has(el)) {
        roleCache.set(el, this.getRole(el));
      }
      return roleCache.get(el)!;
    };
    
    const getName = (el: HTMLElement): string => {
      if (!nameCache.has(el)) {
        nameCache.set(el, this.computeAccessibleName(el));
      }
      return nameCache.get(el)!;
    };
    
    // Sort by DOM depth (deeper elements first) so we process children before parents
    const sortedElements = [...elements].sort((a, b) => {
      return getDepth(b) - getDepth(a); // Deeper elements first
    });
    
    for (const el of sortedElements) {
      if (seen.has(el)) continue;
      
      // Check if this element is dominated by a child we already included
      let dominated = false;
      for (const included of result) {
        // If an included element is a child of this element
        if (el.contains(included) && el !== included) {
          // The child is more specific, skip this parent
          // Unless this parent has a distinct role/action (like a card with a button inside)
          const elRole = getRole(el);
          const includedRole = getRole(included);
          
          // If parent is a card/container and child is a specific action, keep both
          if (elRole === 'card' || elRole === 'listitem' || elRole === 'option') {
            // Keep the card/container if it has a name
            const elName = getName(el);
            if (!elName) {
              dominated = true;
              break;
            }
          } else if (elRole === includedRole) {
            // Same role - prefer the more specific (child)
            dominated = true;
            break;
          }
        }
      }
      
      // Also check if an ancestor is already included and this element has no unique name
      if (!dominated) {
        for (const included of result) {
          if (included.contains(el) && included !== el) {
            // An ancestor is already included
            const elName = getName(el);
            const includedName = getName(included);
            
            // If this element has a different name, keep it (e.g., button inside card)
            if (!elName || elName === includedName) {
              dominated = true;
              break;
            }
          }
        }
      }
      
      if (!dominated) {
        seen.add(el);
        result.push(el);
      }
    }
    
    return result;
  }
  
  private getElementDepth(el: HTMLElement): number {
    let depth = 0;
    let current: HTMLElement | null = el;
    while (current && current !== document.body) {
      depth++;
      current = current.parentElement;
    }
    return depth;
  }
  
  // -------------------------------------------------------------------------
  // Element Processing
  // -------------------------------------------------------------------------
  
  /**
   * Determine if an element is blocked by a modal, collapsed details, or
   * collapsed accordion. Returns the reason string or null if reachable.
   */
  private getBlockedReason(el: HTMLElement): AccessibleElement['hiddenBy'] {
    if (!isAllowedByModals(el)) return 'modal';
    if (isInsideCollapsedDetails(el)) return 'collapsed-details';
    if (isInsideCollapsedAccordion(el, 8)) return 'collapsed-accordion';
    return null;
  }

  private processElement(el: HTMLElement, viewport: { width: number; height: number }): AccessibleElement | null {
    const rect = el.getBoundingClientRect();
    
    // The proxy host's title is its explicit visible state description. Use it
    // directly instead of reading the transparent native input or label markup.
    const name = this.proxyToggleCheckboxes.has(el)
      ? (el.getAttribute('title') || '').trim()
      : this.computeAccessibleName(el);
    let role = this.getRole(el);
    
    // Assign a role to roleless clickables and card-style containers.
    if (!role || role === 'element') {
      // Roleless clickable captured via the cursor:pointer path (drawer tabs,
      // fake <div class="btn"> buttons). Only promote when it has a clear class
      // token AND an accessible name — a nameless icon-only clickable must stay
      // roleless so the drop gate below culls it instead of leaking noise.
      const inferred = (this.clickableDivs.has(el) && name) ? this.inferClickableRole(el) : undefined;
      if (inferred) {
        role = inferred;                 // 'tab' | 'button'
      } else if (this.looksLikeCard(el)) {
        role = 'card';                   // preserve existing card detection
      }
    }

    // Skip elements with no meaningful name and generic role
    if (!name && (!role || role === 'element')) {
      return null;
    }
    
    // Build element info
    const state = this.getState(el);
    const context = this.findContext(el);
    const rowContext = this.getProxyToggleRowContext(el) ?? this.getRowContext(el);
    
    // Generate temporary ref (will be renumbered after sorting)
    const ref = this.generateRef(role || 'element');
    
    // Capture value for form elements
    let value: string | undefined;
    const isPassword = el.localName === 'input' && (el as HTMLInputElement).type === 'password';
    if (el instanceof HTMLInputElement) {
      if (!isPassword && el.value) value = el.value;
    } else if (el instanceof HTMLTextAreaElement && el.value) {
      value = el.value;
    } else if (el instanceof HTMLSelectElement && el.selectedIndex >= 0) {
      // For select, capture the selected option text
      const selectedOption = el.options[el.selectedIndex];
      if (selectedOption) {
        value = selectedOption.text;
      }
    } else if (el.getAttribute('contenteditable') === 'true') {
      // Rich text / contenteditable elements
      const text = (el.textContent || '').trim();
      if (text) {
        value = text.length > 200 ? text.slice(0, 200) + '...' : text;
      }
    }

    // ARIA value attributes (combobox, slider, spinbutton, progressbar)
    if (!value && !isPassword) {
      const ariaValueText = el.getAttribute('aria-valuetext');
      const ariaValueNow = el.getAttribute('aria-valuenow');
      if (ariaValueText) {
        value = ariaValueText;
      } else if (ariaValueNow) {
        value = ariaValueNow;
      }
    }

    // Custom / read-only select display values (Angular ng-select, NZ-Select,
    // Ant Design, React Select). These render the current selection as text in
    // a display node while the exposed element stays empty, so the value is
    // absent from the prompt unless it is read from the display node.
    if (!value && !isPassword) {
      const displayed = this.getCustomSelectDisplayValue(el);
      if (displayed) value = displayed;
    }

    // Date/time picker display values
    if (!value && !isPassword) {
      const isDatePicker = el.classList.contains('ant-picker') ||
        el.closest('.ant-picker') || el.closest('[class*="datepicker"]') ||
        el.closest('[class*="DatePicker"]');
      if (isDatePicker) {
        const dateInput = el.querySelector('.ant-picker-input input, [class*="date-input"] input, input') as HTMLInputElement;
        if (dateInput && dateInput.type !== 'password' && dateInput.value) {
          value = dateInput.value;
        }
      }
    }
    
    // Check if the element is blocked by a modal, collapsed section, etc.
    const hiddenBy = this.getBlockedReason(el);
    // If blocked, the element is not interactable regardless of other checks
    const interactable = hiddenBy ? false : sharedCheckInteractable(el, true);

    // Scroll awareness for scrollable containers
    let scrollInfo: AccessibleElement['scrollInfo'] | undefined;
    const elRole = (role || '').toLowerCase();
    const isScrollableRole = elRole === 'listbox' || elRole === 'menu' || elRole === 'combobox'
      || elRole === 'list' || elRole === 'tree' || elRole === 'grid';
    const isScrollable = el.scrollHeight > el.clientHeight + 10;
    if (isScrollable && (isScrollableRole || el.scrollHeight > el.clientHeight + 50)) {
      scrollInfo = {
        scrollable: true,
        hasMoreAbove: el.scrollTop > 10,
        hasMoreBelow: el.scrollTop + el.clientHeight < el.scrollHeight - 10,
      };
    }

    // Error message extraction for invalid fields
    let errorMessage: string | undefined;
    if (state.invalid) {
      // aria-errormessage points to an element with the error text
      const errMsgId = el.getAttribute('aria-errormessage');
      if (errMsgId) {
        const errEl = document.getElementById(errMsgId);
        if (errEl) errorMessage = (errEl.textContent || '').trim().slice(0, 100);
      }
      // Fallback: look for sibling/nearby error message elements
      if (!errorMessage) {
        const errorSelectors = [
          '.error-message', '.form-error', '.field-error',
          '.ant-form-item-explain-error', '.ant-form-item-explain',
          '.help-block.error', '.invalid-feedback',
          '[class*="error-msg"]', '[class*="ErrorMessage"]',
          '[role="alert"]',
        ];
        const parent = el.closest('.form-group, .ant-form-item, .field, [class*="form-field"]') || el.parentElement;
        if (parent) {
          for (const sel of errorSelectors) {
            try {
              const errEl = parent.querySelector(sel) as HTMLElement;
              if (errEl) {
                const text = (errEl.textContent || '').trim();
                if (text) { errorMessage = text.slice(0, 100); break; }
              }
            } catch { /* skip */ }
          }
        }
      }
    }

    // Live region detection (toasts, alerts, notifications)
    let liveRegion: AccessibleElement['liveRegion'] | undefined;
    const ariaLive = el.getAttribute('aria-live');
    const ariaRole = el.getAttribute('role');
    if (ariaLive === 'assertive' || ariaRole === 'alert') {
      liveRegion = 'assertive';
    } else if (ariaLive === 'polite' || ariaRole === 'status' || ariaRole === 'log') {
      liveRegion = 'polite';
    }

    // Test ID mining — stable selectors for reliable element targeting
    // Check element itself, then walk up ancestors (max 4 levels) for nearest test ID
    let testId: string | undefined;
    const testIdAttrs = ['data-testid', 'data-cy', 'data-automation-id', 'data-qa', 'data-test'];
    let testIdTarget: Element | null = el;
    let testIdDepth = 0;
    while (testIdTarget && testIdDepth < 5) {
      for (const attr of testIdAttrs) {
        const val = testIdTarget.getAttribute(attr);
        if (val) { testId = val; break; }
      }
      if (testId) break;
      testIdTarget = testIdTarget.parentElement;
      testIdDepth++;
    }

    // Input constraints — help LLM know valid input boundaries
    let constraints: Record<string, string> | undefined;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      const c: Record<string, string> = {};
      if (el instanceof HTMLInputElement) {
        if (el.type && el.type !== 'text') c.type = el.type;
        if (el.min) c.min = el.min;
        if (el.max) c.max = el.max;
        if (el.step && el.step !== 'any') c.step = el.step;
        if (el.pattern) c.pattern = el.pattern;
        if (el.accept) c.accept = el.accept;
      }
      if ('maxLength' in el && el.maxLength > 0 && el.maxLength < 524288) {
        c.maxlength = String(el.maxLength);
      }
      if ('minLength' in el && (el as HTMLInputElement).minLength > 0) {
        c.minlength = String((el as HTMLInputElement).minLength);
      }
      if (Object.keys(c).length > 0) constraints = c;
    }

    // Landmark region context — disambiguate identical elements in different page regions
    const landmark = this.findLandmark(el);

    // Position in set — "item X of Y" for lists/grids
    const positionInSet = this.getPositionInSet(el);

    return {
      ref,
      role: role || 'element',
      name: name || this.generateFallbackName(el, role),
      ...(Object.keys(state).length && { state }),
      ...(value && { value }),
      ...(context && { context }),
      ...(rowContext && { rowContext }),
      rect: {
        x: Math.round(rect.x),
        y: Math.round(rect.y),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
      selector: this.generateSelector(el),
      tag: el.tagName.toLowerCase(),
      interactable,
      ...(hiddenBy && { hiddenBy }),
      ...(scrollInfo && { scrollInfo }),
      ...(errorMessage && { errorMessage }),
      ...(liveRegion && { liveRegion }),
      ...(testId && { testId }),
      ...(constraints && { constraints }),
      ...(landmark && { landmark }),
      ...(positionInSet && { positionInSet }),
    };
  }
  
  // -------------------------------------------------------------------------
  // Accessible Name Computation (W3C accname algorithm - simplified)
  // -------------------------------------------------------------------------
  
  // Framework a11y boilerplate that must never become an element name — ag-Grid
  // stamps these on rows/cells for screen readers ("Press SPACE to select this
  // row."), which otherwise drowns out the actual cell value.
  private static readonly ARIA_BOILERPLATE = /press (space|enter)|select this row|to sort|use arrow keys|to toggle/i;

  /** Clean visible text of an ag-Grid cell — prefer its value span, drop boilerplate. */
  private agCellText(cell: Element): string {
    const valueSpan = cell.querySelector('.ag-cell-value');
    const raw = ((valueSpan?.textContent ?? cell.textContent) || '').replace(/\s+/g, ' ').trim();
    if (!raw || AccessibleSnapshotCapture.ARIA_BOILERPLATE.test(raw)) return '';
    return raw.slice(0, 100);
  }

  /**
   * Resolve the value of an ag-Grid cell, tolerating the pinned/center mirror
   * architecture: the queried cell copy is frequently an empty placeholder while
   * a sibling copy (same row-id + col-id) holds the rendered value. Returns null
   * when el isn't an ag-Grid cell, so it's a no-op for every other element.
   */
  private gridCellValue(el: HTMLElement): string | null {
    const cell = el.closest('.ag-cell[col-id]');
    if (!cell) return null;
    const own = this.agCellText(cell);
    if (own) return own;
    const row = el.closest('.ag-row[row-id]');
    const colId = cell.getAttribute('col-id');
    const rowId = row?.getAttribute('row-id');
    if (!colId || !rowId) return null;
    const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
    // Scope to THIS grid instance: pinned/center mirror copies live under the
    // same .ag-root-wrapper, but another grid on the page can reuse the same
    // row-id/col-id (index-based row-ids especially), so a document-wide query
    // could read a cell from the wrong grid.
    const grid: ParentNode = cell.closest('.ag-root-wrapper') ?? document;
    let copies: NodeListOf<Element>;
    try {
      copies = grid.querySelectorAll(
        `.ag-row[row-id="${esc(rowId)}"] .ag-cell[col-id="${esc(colId)}"]`
      );
    } catch {
      return null;
    }
    for (const c of Array.from(copies)) {
      const t = this.agCellText(c);
      if (t) return t;
    }
    return null;
  }

  private computeAccessibleName(el: HTMLElement): string {
    // 0. ag-Grid cell value — the grid mirrors each column across pinned/center
    //    containers and virtualizes rows, so the queried copy is often an empty
    //    placeholder. Read the populated copy (same row-id + col-id) instead.
    const gridText = this.gridCellValue(el);
    if (gridText) return gridText;

    // 1. aria-labelledby (highest priority)
    const labelledBy = el.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labels = labelledBy.split(/\s+/)
        .map(id => document.getElementById(id)?.textContent?.trim())
        .filter(Boolean);
      if (labels.length) return labels.join(' ').slice(0, 100);
    }

    // 2. aria-label (ignore framework a11y boilerplate like ag-Grid row-select hints)
    const ariaLabel = el.getAttribute('aria-label')?.trim();
    if (ariaLabel && !AccessibleSnapshotCapture.ARIA_BOILERPLATE.test(ariaLabel)) {
      return ariaLabel.slice(0, 100);
    }
    
    // 3. For inputs: associated <label>
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || el.getAttribute('role') === 'combobox') {
      const labelText = this.getInputLabel(el);
      if (labelText) return labelText;

      // A custom select's repeated "Select" placeholder is a hint, not the
      // field's identity. Prefer its own visible label for both input and wrapper.
      const ancestorLabel = this.getAncestorLabel(el);
      if (ancestorLabel) return ancestorLabel;

      // Fallback to placeholder attribute
      const placeholder = el.getAttribute('placeholder');
      if (placeholder) return placeholder.slice(0, 100);

      // 3b. Custom select placeholder text (NZ-Select, Ant Design, React Select)
      // These render placeholder in a sibling element, not as an HTML attribute.
      const customPlaceholder = this.getCustomSelectPlaceholder(el);
      if (customPlaceholder) return customPlaceholder;

    }

    // 4. For images: alt text
    if (el instanceof HTMLImageElement) {
      if (el.alt) return el.alt.slice(0, 100);
    }

    // 4b. For SVG: <title> element
    if (el.tagName.toLowerCase() === 'svg' || el instanceof SVGElement) {
      const svgTitle = el.querySelector('title')?.textContent?.trim();
      if (svgTitle) return svgTitle.slice(0, 100);
    }

    // 4c. For tables: <caption>
    if (el instanceof HTMLTableElement) {
      const caption = el.querySelector('caption')?.textContent?.trim();
      if (caption) return caption.slice(0, 100);
    }

    // 4d. For figures: <figcaption>
    if (el.tagName.toLowerCase() === 'figure') {
      const figcaption = el.querySelector('figcaption')?.textContent?.trim();
      if (figcaption) return figcaption.slice(0, 100);
    }

    // 5. Visible control text. A generic title such as "More" must not mask a
    // destructive visible label such as "Delete" on a real button/link.
    const directText = this.getDirectTextContent(el);
    if (directText) return directText;

    // 6. title attribute — fallback for otherwise-empty controls such as
    // LeadRat's roleless icon-badge row actions.
    const title = el.getAttribute('title')?.trim();
    if (title) return title.slice(0, 100);

    // 6b. For containers with images: child <img> alt text
    // Handles card/tile UIs where the label is in an image (e.g., cloud provider tiles)
    const childImg = el.querySelector('img[alt]') as HTMLImageElement | null;
    if (childImg?.alt) return childImg.alt.slice(0, 100);

    // 7. For inputs in MUI/similar: look for sibling label
    const siblingLabel = this.findSiblingLabel(el);
    if (siblingLabel) return siblingLabel;

    // 7b. For switches/toggles in flex rows: label is often a cousin element.
    // Pattern: div.flex > [div > "Label text"] + [div > nz-switch > button]
    // Walk up to the nearest flex container and look for text content in
    // a sibling branch of the button's ancestor.
    if (el.matches('button.ant-switch, [role="switch"], [class*="switch"]')) {
      let ancestor: HTMLElement | null = el.parentElement;
      for (let depth = 0; ancestor && depth < 4; depth++, ancestor = ancestor.parentElement) {
        const style = window.getComputedStyle(ancestor);
        if (style.display === 'flex' || style.display === 'inline-flex') {
          // Found flex container — look for text in sibling children
          for (const child of ancestor.children) {
            if (child.contains(el)) continue; // skip our own branch
            const text = child.textContent?.trim();
            if (text && text.length > 0 && text.length < 60) {
              return text.split('\n')[0]?.trim().slice(0, 100) || '';
            }
          }
          break;
        }
      }
    }

    // 8. For icon buttons: check for SVG title/desc or nearby tooltip text
    const svg = el.querySelector('svg');
    if (svg) {
      const svgTitle = svg.querySelector('title')?.textContent?.trim();
      if (svgTitle) return svgTitle.slice(0, 100);
      
      const svgDesc = svg.querySelector('desc')?.textContent?.trim();
      if (svgDesc) return svgDesc.slice(0, 100);
    }
    
    // 9. Check for tooltip/data attributes (common for icon buttons)
    const tooltip = el.getAttribute('data-tooltip') || 
                    el.getAttribute('data-tip') ||
                    el.getAttribute('data-original-title');
    if (tooltip) return tooltip.slice(0, 100);
    
    // 10. data-testid as last resort (often meaningful)
    const testId = el.getAttribute('data-testid');
    if (testId) {
      // Convert kebab-case or snake_case to readable text
      return testId.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').slice(0, 100);
    }
    
    // 11. Check for common icon class patterns to generate descriptive name
    const iconName = this.extractIconName(el);
    if (iconName) return iconName;

    // 12. CSS pseudo-element content (::before / ::after)
    // Many UI frameworks use pseudo-elements for labels, badges, required asterisks
    try {
      const beforeContent = window.getComputedStyle(el, '::before').content;
      const afterContent = window.getComputedStyle(el, '::after').content;
      const pseudoParts: string[] = [];
      if (beforeContent && beforeContent !== 'none' && beforeContent !== 'normal') {
        // Content is quoted like '"text"' — strip quotes
        const text = beforeContent.replace(/^["']|["']$/g, '').trim();
        if (text && text.length > 1 && text.length < 100) pseudoParts.push(text);
      }
      if (afterContent && afterContent !== 'none' && afterContent !== 'normal') {
        const text = afterContent.replace(/^["']|["']$/g, '').trim();
        if (text && text.length > 1 && text.length < 100) pseudoParts.push(text);
      }
      if (pseudoParts.length > 0) return pseudoParts.join(' ').slice(0, 100);
    } catch { /* getComputedStyle can fail on detached elements */ }

    return '';
  }
  
  private getInputLabel(el: HTMLElement): string {
    // Check for <label for="id">
    if (el.id) {
      const label = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (label) {
        const text = label.textContent?.trim();
        if (text) return text.slice(0, 100);
      }
    }
    
    // Check for wrapping <label>
    const parentLabel = el.closest('label');
    if (parentLabel) {
      const clone = parentLabel.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('input, select, textarea').forEach(e => e.remove());
      const text = clone.textContent?.trim();
      if (text) return text.slice(0, 100);
    }
    
    // Check for aria-describedby
    const describedBy = el.getAttribute('aria-describedby');
    if (describedBy) {
      const desc = document.getElementById(describedBy);
      if (desc) {
        const text = desc.textContent?.trim();
        if (text) return text.slice(0, 100);
      }
    }
    
    return '';
  }
  
  private getDirectTextContent(el: HTMLElement): string {
    // Get text from direct child text nodes only
    let text = '';
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += (node.textContent || '') + ' ';
      }
    }
    text = text.trim();
    
    // If no direct text, check for single text-bearing child
    if (!text) {
      // Common patterns: <button><span>Text</span></button>
      // Also handle MUI and other framework wrappers, and cards with div children
      const textSelectors = [
        ':scope > span',
        ':scope > strong',
        ':scope > em',
        ':scope > b',
        ':scope > label',
        ':scope > .MuiButton-label',        // MUI
        ':scope > .ant-btn-content',        // Ant Design
        ':scope > [class*="label"]',        // Generic
        ':scope > [class*="text"]',         // Generic
        ':scope > div > [class*="font-medium"]',  // Tailwind card titles
        ':scope > div > [class*="title"]',        // Generic card titles
        ':scope > [class*="font-medium"]',        // Direct child title
        ':scope > [class*="title"]',              // Direct child title
        ':scope > div',                           // Fallback: first div child
      ];

      for (const selector of textSelectors) {
        try {
          const textChild = el.querySelector(selector);
          if (textChild) {
            // For leaf nodes or short-text nodes, use textContent directly
            const childText = textChild.textContent?.trim();
            if (childText && childText.length < 100) {
              // If this child has many children, prefer just the first line
              if (textChild.children.length > 2) {
                const firstLine = childText.split('\n')[0]?.trim();
                if (firstLine) { text = firstLine; break; }
              } else {
                text = childText;
                break;
              }
            }
          }
        } catch {
          // Invalid selector, continue
        }
      }
    }
    
    // If still nothing, get first line of innerText (for simple elements)
    if (!text && el.innerText) {
      const lines = el.innerText.trim().split('\n');
      text = lines[0]?.trim() || '';
      
      // If it's a card with multiple lines, include them
      if (lines.length > 1 && lines.length <= 3) {
        text = lines.join(' - ').slice(0, 100);
      }
    }
    
    return text.slice(0, 100);
  }
  
  /**
   * Extract icon name from common icon class patterns
   */
  private extractIconName(el: HTMLElement): string {
    // Check for icon elements inside the button
    const iconEl = el.querySelector('[class*="icon"], [class*="Icon"], svg, i');
    const targetEl = iconEl || el;
    
    const className = typeof targetEl.className === 'string' ? targetEl.className : '';
    
    // Common icon class patterns
    const patterns = [
      /fa-([a-z-]+)/i,           // Font Awesome: fa-search, fa-cog
      /fas? fa-([a-z-]+)/i,      // Font Awesome Solid: fas fa-search
      /far? fa-([a-z-]+)/i,      // Font Awesome Regular
      /icon-([a-z-]+)/i,         // Generic: icon-search
      /([a-z]+)-icon/i,          // Generic: search-icon
      /material-icons/i,         // Material Icons (check inner text)
      /lucide-([a-z-]+)/i,       // Lucide icons
      /heroicon-([a-z-]+)/i,     // Heroicons
      /ri-([a-z-]+)/i,           // Remix icons
      /bi-([a-z-]+)/i,           // Bootstrap Icons
      /bx-([a-z-]+)/i,           // Boxicons
      /bxs-([a-z-]+)/i,          // Boxicons Solid
      /ph-([a-z-]+)/i,           // Phosphor icons
      /tabler-([a-z-]+)/i,       // Tabler icons
      /mdi-([a-z-]+)/i,          // Material Design Icons
    ];
    
    for (const pattern of patterns) {
      const match = className.match(pattern);
      if (match && match[1]) {
        // Convert to readable name
        return match[1].replace(/-/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
      }
    }
    
    // For Material Icons, the icon name is in the text content
    if (className.includes('material-icons') || className.includes('material-symbols')) {
      const iconText = targetEl.textContent?.trim();
      if (iconText && iconText.length < 30) {
        return iconText.replace(/_/g, ' ');
      }
    }
    
    return '';
  }
  
  /**
   * Read the CURRENT displayed value of a custom / read-only select control.
   *
   * ng-select and friends expose an empty (usually readOnly) search input as the
   * interactive element while rendering the actual selection as text in a
   * display node (`.ng-value-label`, `.ant-select-selection-item`, ...). Without
   * this read the prompt shows the control's label and no value, so a support
   * plan cannot report the factual current value and must not invent one.
   *
   * Scope and safety rules (all load-bearing):
   *  - Search ONLY inside the control's own select container. Never read page or
   *    body text, so unrelated copy elsewhere cannot become a "value".
   *  - Exactly one distinct value text must qualify. Two different candidates
   *    mean the association is unresolved -> return null (unknown), never the
   *    first match. Nested duplicates with identical text are one value.
   *  - A placeholder is the ABSENCE of a selection: a placeholder-only control
   *    reports no value rather than reporting "Select"/"Choose".
   *  - Only RENDERED text counts: a hidden value node or a hidden duplicate must
   *    not fabricate a value, and a hidden placeholder must not suppress one.
   *  - Text owned by a NESTED control is never donated to the outer control.
   *  - aria-label / aria-labelledby name the control; they are never the value.
   */
  private getCustomSelectDisplayValue(el: HTMLElement): string | null {
    const container = this.resolveCustomSelectContainer(el);
    if (!container) return null;

    let nodes: HTMLElement[];
    try {
      nodes = Array.from(
        container.querySelectorAll<HTMLElement>(
          AccessibleSnapshotCapture.CUSTOM_SELECT_VALUE_SELECTOR
        )
      );
    } catch {
      return null;
    }

    const texts = new Set<string>();
    for (const node of nodes) {
      if (node.closest(AccessibleSnapshotCapture.CUSTOM_SELECT_PLACEHOLDER_SELECTOR)) continue;
      if (this.hasNestedControlBetween(node, container, el)) continue;
      if (!this.isVisible(node) || isAriaHidden(node)) continue;
      const text = this.readRenderedText(node);
      if (text && text.length <= 100) texts.add(text);
    }

    // Unresolved (zero or several distinct) associations stay unknown.
    if (texts.size !== 1) return null;
    const value = texts.values().next().value as string;

    // A placeholder is the ABSENCE of a selection, but only when the user can
    // actually see it: a hidden placeholder beside a visible selected value
    // must not suppress that value.
    if (this.readVisiblePlaceholderText(container) === value) return null;
    return value;
  }

  /**
   * Resolve the custom-select container that OWNS the exposed element.
   *
   * `closest()` matches the element itself first, and a real ng-select / Ant
   * Design stamps `role="combobox"` on its empty inner search input — so letting
   * the input be its own container read no display node at all. Start from the
   * enclosing control for a search leaf, but keep the element itself when it IS
   * a genuine container holding its own display node (a root nz-select /
   * .ant-select / div[role="combobox"] wrapper with no matching ancestor).
   */
  private resolveCustomSelectContainer(el: HTMLElement): HTMLElement | null {
    const start = this.isCustomSelectSearchLeaf(el) ? el.parentElement : el;
    if (!start) return null;
    try {
      return start.closest(
        AccessibleSnapshotCapture.CUSTOM_SELECT_CONTAINER_SELECTOR
      ) as HTMLElement | null;
    } catch {
      return null;
    }
  }

  /**
   * True when the exposed element is the control's SEARCH LEAF rather than a
   * container: a native input/textarea/select, or a combobox-role element that
   * renders no display node of its own. Those must resolve to the enclosing
   * control; anything else keeps itself so a root container's value is read.
   */
  private isCustomSelectSearchLeaf(el: HTMLElement): boolean {
    if (el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement) {
      return true;
    }
    if (el.getAttribute('role') === 'combobox') {
      try {
        return el.querySelector(
          AccessibleSnapshotCapture.CUSTOM_SELECT_VALUE_SELECTOR
        ) === null;
      } catch {
        return false;
      }
    }
    return false;
  }

  /**
   * True when a nested control (a different widget) sits between `node` and the
   * owning `container`, so `node` belongs to that nested widget rather than to
   * the control being read. Ancestors that contain `el` are the owning widget's
   * own wrapper chain (`ng-select` > `.ng-select-container` > ...) and never
   * disqualify the node.
   */
  private hasNestedControlBetween(node: HTMLElement, container: HTMLElement, el: HTMLElement): boolean {
    let ancestor = node.parentElement;
    while (ancestor && ancestor !== container) {
      if (ancestor.matches(AccessibleSnapshotCapture.NESTED_CONTROL_SELECTOR) &&
          !ancestor.contains(el)) {
        return true;
      }
      ancestor = ancestor.parentElement;
    }
    return false;
  }

  /**
   * Rendered text of a placeholder the user can actually see, or '' when the
   * container shows no placeholder.
   */
  private readVisiblePlaceholderText(container: HTMLElement): string {
    let placeholders: HTMLElement[];
    try {
      placeholders = Array.from(
        container.querySelectorAll<HTMLElement>(
          AccessibleSnapshotCapture.CUSTOM_SELECT_PLACEHOLDER_SELECTOR
        )
      );
    } catch {
      return '';
    }

    for (const placeholder of placeholders) {
      if (!this.isVisible(placeholder) || isAriaHidden(placeholder)) continue;
      const text = this.readRenderedText(placeholder);
      if (text) return text;
    }
    return '';
  }

  /**
   * Rendered text as the user sees it: hidden subtrees (`display:none`,
   * `visibility:hidden`, `[hidden]`, `aria-hidden="true"`) contribute nothing,
   * so a hidden option or a hidden duplicate value can never be promoted into
   * the captured value. Bounded to `maxLength` characters.
   */
  private readRenderedText(el: HTMLElement, maxLength = 100): string {
    const parts: string[] = [];
    let length = 0;
    const walk = (node: Node): void => {
      if (length >= maxLength) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        parts.push(text);
        length += text.length;
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const child = node as HTMLElement;
      if (child.hasAttribute('hidden') || child.getAttribute('aria-hidden') === 'true') return;
      try {
        const style = window.getComputedStyle(child);
        if (style.display === 'none' || style.visibility === 'hidden') return;
      } catch {
        // No computed style available (rare); fall through and read the text.
      }
      for (const grandchild of Array.from(child.childNodes)) walk(grandchild);
    };

    for (const child of Array.from(el.childNodes)) walk(child);
    // Concatenate with NO separator. Adjacent inline fragments render with no
    // space between them (`<b>INR</b><b>(Rs)</b>` reads "INR(Rs)"), so joining
    // with a separator invents whitespace the page never had and corrupts the
    // value. Any whitespace the source really contains is already a text node in
    // `parts`; the normalisation below collapses it to a single space.
    return parts.join('').replace(/\s+/g, ' ').trim().slice(0, maxLength);
  }

  /**
   * True for a node that RENDERS a value rather than acting as a control: short
   * text, no interactive descendants, no form-control semantics. Used to bound
   * which elements may pick up a preceding heading as their label — a bare
   * displayed value ("INR (₹)") is otherwise anonymous, while a control already
   * resolves its own name.
   */
  private isDisplayValueNode(el: HTMLElement): boolean {
    if (el.matches('input, select, textarea, button, a[href]')) return false;
    if (el.querySelector(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)) return false;
    if (!this.isVisible(el) || isAriaHidden(el)) return false;
    const text = this.readRenderedText(el, 41);
    return text.length > 0 && text.length <= 40;
  }

  /**
   * For inputs inside custom select components (NZ-Select, Ant Design, React Select),
   * the placeholder text is rendered in a sibling/cousin element, not as an HTML
   * placeholder attribute. Walk up to the select container and find it.
   */
  private getCustomSelectPlaceholder(el: HTMLElement): string {
    // Walk up to find the custom select container
    const selectContainer = el.closest(
      'nz-select, .ant-select, [class*="select-container"], [class*="Select"], [class*="react-select"]'
    );
    if (!selectContainer) return '';

    // Look for placeholder elements inside the container
    const placeholderSelectors = [
      'nz-select-placeholder',
      '.ant-select-selection-placeholder',
      '[class*="placeholder"]:not(input)',
      '[class*="Placeholder"]:not(input)',
    ];
    for (const sel of placeholderSelectors) {
      try {
        const placeholder = selectContainer.querySelector(sel);
        if (placeholder) {
          const text = (placeholder.textContent || '').trim();
          if (text && text.length < 100) return text;
        }
      } catch { /* skip */ }
    }

    // Also check the container's own placeholder/aria-label
    const containerLabel = selectContainer.getAttribute('aria-label') ||
      selectContainer.getAttribute('placeholder') ||
      selectContainer.getAttribute('placeholdertext');
    if (containerLabel) return containerLabel.slice(0, 100);

    return '';
  }

  /**
   * Walk up from an input element to find a nearby label/heading that
   * describes this field. Handles patterns like:
   *   <div class="tw-mb-3">
   *     <div class="tw-font-semibold">Choose Colleague <span>*</span></div>
   *     <kr-employee-select-dropdown>
   *       <nz-select><input .../></nz-select>
   *     </kr-employee-select-dropdown>
   *   </div>
   */
  private getAncestorLabel(el: HTMLElement): string {
    // Walk up to 6 levels looking for a form field wrapper with a label
    let ancestor = el.parentElement;
    let depth = 0;
    while (ancestor && ancestor !== document.body && depth < 6) {
      // Look for a label-like preceding sibling or child of the ancestor
      const labelSelectors = [
        ':scope > label',
        ':scope > .label',
        ':scope > [class*="label"]',
        ':scope > [class*="Label"]',
        ':scope > [class*="font-semibold"]',
        ':scope > [class*="form-label"]',
        ':scope > [data-cy*="label"]',
      ];
      for (const sel of labelSelectors) {
        try {
          const label = ancestor.querySelector(sel);
          if (label && !label.contains(el)) {
            const text = (label.textContent || '').replace(/\s*\*\s*$/, '').trim();
            if (text && text.length > 1 && text.length < 60) return text;
          }
        } catch { /* skip */ }
      }

      // Check nz-form-label / ant-form-item-label
      try {
        const formLabel = ancestor.querySelector(
          'nz-form-label, .ant-form-item-label, .ant-form-item-label label'
        );
        if (formLabel && !formLabel.contains(el)) {
          const text = (formLabel.textContent || '').replace(/\s*\*\s*$/, '').trim();
          if (text && text.length > 1 && text.length < 60) return text;
        }
      } catch { /* skip */ }

      ancestor = ancestor.parentElement;
      depth++;
    }
    return '';
  }

  private findSiblingLabel(el: HTMLElement): string {
    // Look for preceding sibling that looks like a label
    let sibling = el.previousElementSibling;
    while (sibling) {
      if (sibling.matches('label, .label, [class*="label"], [class*="Label"]')) {
        const text = sibling.textContent?.trim();
        if (text && text.length < 50) return text;
      }
      sibling = sibling.previousElementSibling;
    }
    
    // Look in parent for label-like elements
    const parent = el.parentElement;
    if (parent) {
      const label = parent.querySelector(':scope > label, :scope > .label, :scope > [class*="FormLabel"]');
      if (label && !label.contains(el)) {
        const text = label.textContent?.trim();
        if (text && text.length < 50) return text;
      }
    }
    
    return '';
  }
  
  private generateFallbackName(el: HTMLElement, role: string | null): string {
    const tag = el.tagName.toLowerCase();

    // ag-Grid cell with no readable value (empty mirror / mid-virtualization):
    // name it by its column, which is meaningful ("Name", "Assign To", "Status"),
    // rather than a positional "gridcell 2 in <row boilerplate>".
    const agCell = el.closest('.ag-cell[col-id]');
    if (agCell) {
      const colId = (agCell.getAttribute('col-id') || '').trim();
      // col-id "0" is the checkbox column; skip it
      if (colId && colId !== '0') {
        // "AssignTo" -> "Assign To"
        const label = colId.replace(/([a-z])([A-Z])/g, '$1 $2');
        return `${label} cell`.slice(0, 60);
      }
    }

    // Generate a descriptive name for unnamed elements
    const index = this.getSiblingIndex(el);
    const parentContext = this.getParentContext(el);

    const parts: string[] = [];

    // Use role or tag
    parts.push(role || tag);

    // Add index if there are siblings
    if (index > 0) {
      parts.push(`${index + 1}`);
    }

    // Add parent context
    if (parentContext) {
      parts.push(`in ${parentContext}`);
    }

    return parts.join(' ');
  }
  
  private getSiblingIndex(el: HTMLElement): number {
    const parent = el.parentElement;
    if (!parent) return 0;
    
    const siblings = Array.from(parent.children).filter(
      child => child.tagName === el.tagName
    );
    
    return siblings.indexOf(el);
  }
  
  private getParentContext(el: HTMLElement): string {
    // Find nearest named ancestor, skipping framework a11y boilerplate
    // (e.g. ag-Grid's "Press SPACE to select this row." on rows).
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const ariaLabel = parent.getAttribute('aria-label');
      if (ariaLabel && !AccessibleSnapshotCapture.ARIA_BOILERPLATE.test(ariaLabel)) {
        return ariaLabel.slice(0, 30);
      }
      const title = parent.getAttribute('title');
      if (title) return title.slice(0, 30);
      if (parent.matches('nav, main, header, footer, aside, section, article')) {
        return parent.tagName.toLowerCase();
      }
      parent = parent.parentElement;
    }
    return '';
  }
  
  // -------------------------------------------------------------------------
  // Role Detection
  // -------------------------------------------------------------------------
  
  private getRole(el: HTMLElement): string {
    if (this.proxyToggleCheckboxes.has(el)) return 'switch';

    // Explicit role takes precedence
    const explicitRole = el.getAttribute('role');
    if (explicitRole) return explicitRole;

    const tag = el.tagName.toLowerCase();

    // Handle inputs specially
    if (tag === 'input') {
      const type = (el as HTMLInputElement).type || 'text';
      return AccessibleSnapshotCapture.INPUT_TYPE_ROLES[type] || 'textbox';
    }

    // Handle links (need href to be a link)
    if (tag === 'a') {
      return el.hasAttribute('href') ? 'link' : '';
    }

    // Rich text editor detection — these need special interaction (contenteditable, not input)
    const classes = typeof el.className === 'string' ? el.className : '';
    if (el.getAttribute('contenteditable') === 'true') {
      const editorPatterns = ['ql-editor', 'ProseMirror', 'tiptap', 'ck-editor__editable', 'DraftEditor-content'];
      for (const p of editorPatterns) {
        if (classes.includes(p) || el.closest(`.${p}`)) return 'richtext';
      }
    }
    // Also detect editor containers
    const editorContainers = ['ql-container', 'ck-editor__main', 'DraftEditor-root', 'tiptap-editor'];
    for (const p of editorContainers) {
      if (classes.includes(p)) return 'richtext';
    }

    // Check implicit roles
    return AccessibleSnapshotCapture.IMPLICIT_ROLES[tag] || '';
  }
  
  /**
   * Classify a roleless clickable (captured via the cursor:pointer path) into a
   * concrete role from its class tokens, or undefined when there is no clear
   * signal (so it falls through to card detection / the drop gate). Narrow tokens
   * only: generic 'pill'/'segment'/'badge' are intentionally excluded to avoid
   * mislabeling status chips; the exact titled icon-badge action is a button.
   */
  private inferClickableRole(el: HTMLElement): string | undefined {
    const c = (typeof el.className === 'string' ? el.className : '').toLowerCase();
    if (/nav-item|nav-bar-item|tab-item|\btab\b/.test(c)) return 'tab';
    if (/\bbtn\b/.test(c)) return 'button';
    if (/\bicon-badge\b/.test(c) && !!el.getAttribute('title')?.trim()) {
      return 'button';
    }
    return undefined;
  }

  private looksLikeCard(el: HTMLElement): boolean {
    // className might be SVGAnimatedString for SVG elements
    const classes = typeof el.className === 'string' ? el.className.toLowerCase() : '';
    
    // Check class names
    if (classes.includes('card') || 
        classes.includes('tile') || 
        classes.includes('option') ||
        classes.includes('selection')) {
      return true;
    }
    
    // Check for card-like styling
    const style = window.getComputedStyle(el);
    // borderWidth can be "1px 2px 3px 4px" - check if any value is non-zero
    const borderWidthStr = style.borderWidth || '0';
    const hasBorder = !borderWidthStr.split(' ').every(w => w === '0px' || w === '0') && 
                      style.borderStyle !== 'none';
    // borderRadius can also be shorthand
    const borderRadiusStr = style.borderRadius || '0';
    const hasRoundedCorners = parseFloat(borderRadiusStr) > 0;
    const hasBackground = style.backgroundColor !== 'rgba(0, 0, 0, 0)' && 
                          style.backgroundColor !== 'transparent';
    
    // If it has border + rounded corners + some height, probably a card
    const rect = el.getBoundingClientRect();
    if (hasBorder && hasRoundedCorners && rect.height > 50 && rect.width > 100) {
      return true;
    }
    
    return false;
  }
  
  // -------------------------------------------------------------------------
  // State Detection
  // -------------------------------------------------------------------------
  
  private getState(el: HTMLElement): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    
    // ARIA states
    if (el.getAttribute('aria-selected') === 'true') {
      state.selected = true;
    }
    
    // Native checkbox state is binary evidence. Preserve false explicitly so
    // a rendered unchecked control is distinguishable from a control omitted
    // from the snapshot. Radios retain the existing positive-only treatment.
    const proxyCheckbox = this.proxyToggleCheckboxes.get(el);
    const isCheckbox = el instanceof HTMLInputElement && el.type === 'checkbox';
    const isCheckedElement = el instanceof HTMLInputElement &&
                             (el.type === 'checkbox' || el.type === 'radio');
    if (proxyCheckbox) {
      state.checked = proxyCheckbox.checked;
    } else if (isCheckbox) {
      state.checked = (el as HTMLInputElement).checked;
    } else if (['checkbox', 'switch'].includes(el.getAttribute('role') || '') &&
        el.getAttribute('aria-checked') === 'false') {
      state.checked = false;
    } else if (el.getAttribute('aria-checked') === 'true' ||
        (isCheckedElement && (el as HTMLInputElement).checked)) {
      state.checked = true;
    }
    
    if (el.getAttribute('aria-expanded') === 'true') {
      state.expanded = true;
    }
    
    // Handle <details> element open state
    if (el instanceof HTMLDetailsElement && el.open) {
      state.expanded = true;
    }
    if (el.getAttribute('aria-pressed') === 'true') {
      state.pressed = true;
    }
    
    // Check for disabled state (form elements and buttons)
    const supportsDisabled = el instanceof HTMLButtonElement || 
                             el instanceof HTMLInputElement || 
                             el instanceof HTMLSelectElement ||
                             el instanceof HTMLTextAreaElement ||
                             el instanceof HTMLFieldSetElement;
    if (el.getAttribute('aria-disabled') === 'true' || 
        (supportsDisabled && (el as HTMLButtonElement).disabled) ||
        !!proxyCheckbox?.disabled || el.hasAttribute('disabled')) {
      state.disabled = true;
    }
    
    if (el.getAttribute('aria-current') === 'true' ||
        el.getAttribute('aria-current') === 'page') {
      state.current = true;
    }
    
    // Visual selection detection (for cards without ARIA)
    if (!state.selected && this.detectVisualSelection(el)) {
      state.selected = true;
    }
    
    // Class-based states (className might be SVGAnimatedString for SVG elements)
    const classes = typeof el.className === 'string' ? el.className.toLowerCase() : '';
    if (!state.selected && (
      classes.includes('selected') || 
      classes.includes('active') || 
      classes.includes('chosen') ||
      classes.includes('current')
    )) {
      state.selected = true;
    }
    if (!state.disabled && classes.includes('disabled')) {
      state.disabled = true;
    }

    // Read-only state travels WITH the value. ng-select exposes a readOnly
    // search input as the control, and a support plan that reads the displayed
    // value must also be able to see that the control is not editable rather
    // than inferring it from the markup.
    if (el.getAttribute('aria-readonly') === 'true' ||
        ((el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
         (el as HTMLInputElement).readOnly)) {
      state.readonly = true;
    }

    // Angular ng-select stamps the disabled state on its host element, not on
    // the inner input it exposes.
    if (!state.disabled && el.closest('.ng-select-disabled')) {
      state.disabled = true;
    }
    
    // Data attributes (common in headless UI libraries)
    const dataState = el.getAttribute('data-state');
    if (dataState === 'checked' || dataState === 'on') {
      state.checked = true;
    }
    if (dataState === 'open' || dataState === 'expanded') {
      state.expanded = true;
    }
    if (dataState === 'active' || dataState === 'selected') {
      state.selected = true;
    }

    // Error/validation state
    if (el.getAttribute('aria-invalid') === 'true' ||
        classes.includes('invalid') || classes.includes('error') ||
        classes.includes('has-error') || classes.includes('ng-invalid') ||
        classes.includes('ant-form-item-has-error')) {
      state.invalid = true;
    }

    // Loading/busy state
    if (el.getAttribute('aria-busy') === 'true' ||
        classes.includes('loading') || classes.includes('spinner') ||
        classes.includes('skeleton') || classes.includes('pending') ||
        classes.includes('ant-btn-loading')) {
      state.loading = true;
    }

    // Required state
    if (el.hasAttribute('required') || el.getAttribute('aria-required') === 'true') {
      state.required = true;
    }

    return state;
  }
  
  private detectVisualSelection(el: HTMLElement): boolean {
    const style = window.getComputedStyle(el);
    
    // Check for blue/primary border (common selection pattern)
    const borderColor = style.borderColor.toLowerCase();
    if (borderColor.includes('rgb(8, 103, 236)') ||   // #0867EC
        borderColor.includes('rgb(59, 130, 246)') ||  // blue-500
        borderColor.includes('rgb(37, 99, 235)') ||   // blue-600
        borderColor.includes('rgb(29, 78, 216)')) {   // blue-700
      return true;
    }
    
    // Check for light blue background
    const bgColor = style.backgroundColor.toLowerCase();
    if (bgColor.includes('rgb(238, 245, 252)') ||    // Light blue
        bgColor.includes('rgb(239, 246, 255)') ||    // blue-50
        bgColor.includes('rgb(219, 234, 254)')) {    // blue-100
      return true;
    }
    
    return false;
  }
  
  // -------------------------------------------------------------------------
  // Context Detection
  // -------------------------------------------------------------------------
  
  private findContext(el: HTMLElement): string | null {
    // 1. aria-describedby
    const describedBy = el.getAttribute('aria-describedby');
    if (describedBy) {
      const desc = document.getElementById(describedBy);
      if (desc) return desc.textContent?.trim().slice(0, 50) || null;
    }
    
    // 2. Fieldset legend
    const fieldset = el.closest('fieldset');
    if (fieldset) {
      const legend = fieldset.querySelector('legend');
      if (legend) return legend.textContent?.trim().slice(0, 50) || null;
    }
    
    // 3. Form group label
    const formGroup = el.closest('[class*="form-group"], [class*="FormGroup"], [class*="field"]');
    if (formGroup) {
      const label = formGroup.querySelector(':scope > label, :scope > .label');
      if (label && !label.contains(el)) {
        return label.textContent?.trim().slice(0, 50) || null;
      }
    }
    
    // 4. Section heading
    const section = el.closest('section, [role="region"], form, [class*="section"], [class*="Section"]');
    if (section) {
      const heading = section.querySelector(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > [role="heading"]');
      if (heading) return heading.textContent?.trim().slice(0, 50) || null;
      
      // Check for title/header in section
      const title = section.querySelector(':scope > [class*="title"], :scope > [class*="Title"], :scope > [class*="header"] > *');
      if (title) return title.textContent?.trim().slice(0, 50) || null;
    }
    
    // 5. Preceding sibling that looks like a label/heading
    const prevSibling = el.previousElementSibling;
    if (prevSibling && prevSibling.matches('p, label, h1, h2, h3, h4, h5, h6, [class*="label"], [class*="Label"]')) {
      const text = prevSibling.textContent?.trim();
      if (text && text.length < 50) return text;
    }
    
    // 6. Parent's preceding sibling (common for form groups). A control keeps
    //    the original permissive behaviour; a bare displayed VALUE may only
    //    inherit a label that is visible and labels exactly one value.
    const parentPrev = el.parentElement?.previousElementSibling;
    if (parentPrev && parentPrev.matches('p, label, [class*="label"], [class*="Label"]') &&
        this.mayInheritValueLabel(el, parentPrev)) {
      const text = parentPrev.textContent?.trim();
      if (text && text.length < 50) return text;
    }

    // 7. Label immediately preceding a bare displayed VALUE. LeadRat's General
    //    Settings renders "Default Currency:" as an <h6> sibling of the chip
    //    that carries the value ("INR (₹)"), so without this the reader sees a
    //    currency code with no idea which setting it belongs to. Bounded to one
    //    VISIBLE short sibling text, gated on value-display nodes only, and
    //    abandoned when the heading sits above several value chips — there the
    //    association is ambiguous and must stay unknown rather than label every
    //    chip identically. A control resolves its own name; page-wide copy is
    //    never consulted.
    const valuePrev = el.parentElement?.previousElementSibling;
    if (valuePrev instanceof HTMLElement && this.isDisplayValueNode(el) &&
        valuePrev.matches('h1, h2, h3, h4, h5, h6, [role="heading"]') &&
        this.mayInheritValueLabel(el, valuePrev)) {
      const text = this.readRenderedText(valuePrev, 50);
      if (text && text.length > 1 && text.length < 50) return text;
    }

    return null;
  }

  /**
   * Whether `el` may take `labelEl` as its label.
   *
   * Controls resolve their own accessible name, so this only restricts bare
   * displayed VALUE nodes: the heading must be one the user can actually see,
   * and it must label exactly one value. A hidden heading, or one heading above
   * several value chips, leaves the association ambiguous — unknown, never the
   * first match.
   */
  private mayInheritValueLabel(el: HTMLElement, labelEl: Element): boolean {
    if (!this.isDisplayValueNode(el)) return true;
    if (!(labelEl instanceof HTMLElement)) return false;
    if (!this.isVisible(labelEl) || isAriaHidden(labelEl)) return false;
    return this.countSiblingDisplayValues(el) === 1;
  }

  /**
   * Number of displayed-value chips sharing `el`'s immediate parent, including
   * `el` itself. One heading labels one value: when several chips sit under the
   * same heading ("Default Currency:" above two currency chips) the association
   * is ambiguous, so callers must leave it unknown instead of applying the
   * heading to all of them.
   */
  private countSiblingDisplayValues(el: HTMLElement): number {
    const parent = el.parentElement;
    if (!parent) return 0;
    let count = 0;
    for (const child of Array.from(parent.children)) {
      if (child === el || (child instanceof HTMLElement && this.isDisplayValueNode(child))) {
        count++;
      }
    }
    return count;
  }

  /**
   * Give every duplicate-named element the label of the component that owns it.
   *
   * A grid of cards whose only control is a same-worded button ("Connect Now",
   * "Select plan", "Install") collapses to N identical entries in the prompt —
   * the card's own title is a SIBLING of the control, so neither
   * computeAccessibleName nor findContext ever reaches it. The LLM then cannot
   * name the one it wants and picks the first, silently. (LeadRat's
   * /global-config ships 16 such cards.)
   *
   * Only duplicates pay for this: unique names are already unambiguous, and the
   * DOM walk is the expensive part.
   */
  private addOwnerContextForDuplicates(
    elements: AccessibleElement[],
    sourceNodes: Map<AccessibleElement, HTMLElement>
  ): void {
    const counts = new Map<string, number>();
    for (const info of elements) {
      const key = (info.name || '').trim().toLowerCase();
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    }

    for (const info of elements) {
      if (info.context) continue; // already disambiguated by findContext
      const key = (info.name || '').trim().toLowerCase();
      if (!key || (counts.get(key) || 0) < 2) continue;

      const node = sourceNodes.get(info);
      if (!node) continue;
      try {
        const owner = this.deriveOwnerLabel(node);
        if (owner) info.context = owner;
      } catch { /* never let disambiguation break a capture */ }
    }
  }

  /**
   * Derive a label for the element from the nearest ancestor that adds text.
   *
   * Walks up nearest-first and returns the first ancestor's text MINUS the
   * element's own text — for `<card><h4>Housing</h4><div>Connect Now</div></card>`
   * that yields "Housing". Nearest-first matters: the tightest wrapper is the
   * one whose extra text actually identifies this element.
   *
   * Two ways an ancestor proves it wraps our siblings rather than just us, both
   * of which mean STOP (everything above is shared too, so climbing only makes
   * it worse):
   *
   *  - our own text appears in it more than once — flat `<span>X</span><button>Buy</button>`
   *    `<span>Y</span><button>Buy</button>` markup subtracts "Buy" from both items at
   *    once and leaves "X Y", short enough to pass the length guard and be stamped
   *    on every duplicate. A shared label is worse than none: it is wrong on its
   *    face AND, being identical, it suppresses the coarse-position fallback the
   *    server would otherwise print for duplicates.
   *  - what is left is too long to be one item's label.
   */
  private deriveOwnerLabel(el: HTMLElement): string | null {
    const norm = (s: string | null) => (s || '').replace(/\s+/g, ' ').trim();
    const own = norm(el.textContent);

    let current = el.parentElement;
    for (let depth = 0; current && depth < 6; depth++, current = current.parentElement) {
      if (current === document.body || current === document.documentElement) break;

      const full = norm(current.textContent);
      if (!full || full === own) continue; // this level adds nothing — keep climbing

      if (!own) return full.length <= 60 ? full : null;

      const parts = full.split(own);
      if (parts.length > 2) return null; // our text repeats here — these are peers

      const extra = norm(parts.join(' '));
      if (!extra) continue;
      return extra.length <= 60 ? extra : null;
    }
    return null;
  }

  /**
   * Find which landmark region an element belongs to (nav, main, header, footer, etc.).
   * Helps disambiguate identical elements in different page regions — e.g. a "Leads"
   * link in the sidebar vs a "Leads" column header in a data grid.
   */
  private findLandmark(el: HTMLElement): string | undefined {
    // 1. INNERMOST specific region wins. inferRegion walks nearest-first over
    //    role/tag/class conventions (data table, dialog, navigation, form, …).
    //    This MUST run before the semantic-landmark closest() below: on apps that
    //    wrap everything in a single <main> (React SaaS like Rocketlane), a bare
    //    closest('main') would shadow a nearer ag-Grid ("data table") or a portaled
    //    modal ("dialog"), collapsing the whole page to @main and killing
    //    within-page disambiguation. The specific inner region is always the more
    //    useful hint. @region is only a HINT to the LLM, never a click target, so a
    //    heuristic mis-tag can mislead wording but can never cause a wrong action.
    const inferred = this.inferRegion(el);
    if (inferred) return inferred;

    // 2. Fallback: nearest coarse semantic landmark (main/aside/banner/…) when no
    //    finer region classifies — better than nothing for plain content, and
    //    carries aria-label specificity (e.g. "navigation: Primary navigation").
    const landmarkEl = el.closest(
      'nav, main, aside, header, footer, ' +
      '[role="navigation"], [role="banner"], [role="main"], ' +
      '[role="contentinfo"], [role="complementary"], [role="search"], [role="form"]'
    );
    if (landmarkEl) {
      const label = landmarkEl.getAttribute('aria-label');
      const role = landmarkEl.getAttribute('role') ||
        AccessibleSnapshotCapture.IMPLICIT_ROLES[landmarkEl.tagName.toLowerCase()] || '';
      if (label) return `${role}: ${label}`.slice(0, 50);
      if (role) return role;
    }
    return undefined;
  }

  /**
   * Walk ancestors nearest-first to classify an element into a UI region.
   *
   * Innermost region wins so nested *content* structures resolve correctly
   * (a mini-table inside a drawer → "data table", not "dialog"). BUT a *chrome*
   * region (navigation / header / toolbar / menu / footer) nested inside a
   * dialog is misleading — a drawer's own tab bar (e.g. LeadRat's
   * `div.horizontal-navbar` of Overview/Status/History tabs) is NOT the app's
   * main navigation, and tagging it "navigation" would collide it with the
   * sidebar in the LLM's mind. So when the innermost region is chrome and any
   * ancestor is a dialog, resolve to "dialog" — this disambiguates same-named
   * items by container while leaving content regions (data table, form) intact.
   */
  private inferRegion(el: HTMLElement): string | undefined {
    const CHROME = new Set(['navigation', 'header', 'toolbar', 'menu', 'footer']);
    let node: HTMLElement | null = el;
    let depth = 0;
    let innermost: string | undefined;
    while (node && node !== document.body && depth < 20) {
      const region = AccessibleSnapshotCapture.classifyRegion(node);
      if (region) {
        if (!innermost) {
          innermost = region;
          // A content/dialog innermost region is authoritative — return at once.
          if (!CHROME.has(region)) return region;
        }
        // Chrome innermost, but an enclosing dialog outranks it for disambiguation.
        if (region === 'dialog') return 'dialog';
      }
      node = node.parentElement;
      depth++;
    }
    return innermost;
  }

  /** Classify a single element into a coarse UI region, or null if it carries no signal. */
  private static classifyRegion(node: HTMLElement): string | undefined {
    const role = (node.getAttribute('role') || '').toLowerCase();
    if (role === 'dialog' || role === 'alertdialog') return 'dialog';
    if (role === 'grid' || role === 'treegrid' || role === 'table') return 'data table';
    if (role === 'toolbar') return 'toolbar';
    if (role === 'menu' || role === 'menubar') return 'menu';

    const tag = node.tagName.toLowerCase();
    if (tag === 'table') return 'data table';
    if (tag === 'dialog') return 'dialog';
    if (tag === 'form') return 'form';

    const cls = (typeof node.className === 'string'
      ? node.className
      : node.getAttribute('class') || '').toLowerCase();
    if (!cls) return undefined;

    // ag-Grid (any ag-* structural class) and generic data grids/tables
    if (/\bag-(root|body|header|center|pinned|cell|row|grid|floating)|ag-grid|data-?grid|datatable|data-?table|\btable\b|list-?view|grid-view/.test(cls)) return 'data table';
    // navigation / sidebars / nav-menus — deliberately NOT a bare "menu" token,
    // which false-matches UI-kit dropdowns (mat-menu-trigger, dropdown-menu).
    if (/sidebar|side-?nav|sidenav|navbar|nav-|nav_|\bnav\b|navigation|left-?nav|main-?menu|nav-?menu|menu-?bar/.test(cls)) return 'navigation';
    // modals / drawers / dialogs / popovers
    if (/modal|drawer|\bdialog\b|popover|flyout|off-?canvas|overlay-panel/.test(cls)) return 'dialog';
    // top bars / app headers / toolbars (NOT bare "header" — that catches table headers)
    if (/topbar|top-bar|app-?bar|appbar|page-?header|app-?header|header-?bar|\btoolbar\b|action-?bar/.test(cls)) return 'header';
    // footers
    if (/\bfooter\b/.test(cls)) return 'footer';
    return undefined;
  }

  /**
   * Get position in set for list/grid items — "3 of 12"
   */
  private getPositionInSet(el: HTMLElement): string | undefined {
    // Check ARIA attributes first (authoritative)
    const ariaPosInSet = el.getAttribute('aria-posinset');
    const ariaSetSize = el.getAttribute('aria-setsize');
    if (ariaPosInSet && ariaSetSize) {
      return `${ariaPosInSet} of ${ariaSetSize}`;
    }

    const elRole = (el.getAttribute('role') || '').toLowerCase();
    const tag = el.tagName.toLowerCase();

    // For option/menuitem/treeitem/tab/listitem, count siblings
    const isSetMember = elRole === 'option' || elRole === 'menuitem' ||
      elRole === 'menuitemcheckbox' || elRole === 'menuitemradio' ||
      elRole === 'treeitem' || elRole === 'tab' || elRole === 'listitem' ||
      tag === 'option' || tag === 'li';

    if (!isSetMember) return undefined;

    const parent = el.parentElement;
    if (!parent) return undefined;

    // Build a selector that matches siblings of the same type
    let siblingSelector: string;
    if (elRole) {
      siblingSelector = `:scope > [role="${elRole}"]`;
    } else if (tag === 'option') {
      siblingSelector = ':scope > option';
    } else {
      siblingSelector = `:scope > ${tag}`;
    }

    try {
      const siblings = Array.from(parent.querySelectorAll(siblingSelector));
      if (siblings.length <= 1) return undefined;
      const index = siblings.indexOf(el);
      if (index >= 0) return `${index + 1} of ${siblings.length}`;
    } catch { /* invalid selector */ }

    return undefined;
  }

  /** Derive a short owner label only for a qualified proxy toggle. */
  private getProxyToggleRowContext(el: HTMLElement): string | null {
    if (!this.proxyToggleCheckboxes.has(el) || !el.parentElement) return null;

    // A direct sibling is the only context we accept. Walking farther out can
    // accidentally attach page-wide copy to an otherwise unambiguous toggle.
    const normalize = (text: string) => text.replace(/\s+/g, ' ').trim();
    const labels = Array.from(el.parentElement.children).filter(child => {
      if (!(child instanceof HTMLElement) || child.contains(el) || !this.isVisible(child)) return false;
      // A neighboring action is not the setting's owner label, even when its
      // caption is the only other text in the row.
      if (child.matches(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR) ||
          child.querySelector(AccessibleSnapshotCapture.INTERACTIVE_SELECTOR)) return false;
      const rect = child.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && !!normalize(child.innerText || child.textContent || '').length;
    }).map(child => normalize((child as HTMLElement).innerText || child.textContent || '')).filter(text => text.length <= 80);
    const unique = [...new Set(labels)];
    return unique.length === 1 ? unique[0] : null;
  }

  private getRowContext(el: HTMLElement): string | null {
    // Check if element is in a table row
    const row = el.closest('tr, [role="row"]');
    if (row) {
      // Use the first meaningful identifier cell. Selection/checkbox columns are
      // commonly empty, so reading only :first-child drops the account or record
      // name that safely disambiguates repeated row actions.
      const cells = row.querySelectorAll<HTMLElement>(
        ':scope > td, :scope > th, :scope > [role="cell"], :scope > [role="rowheader"]'
      );
      for (const cell of cells) {
        if (cell.contains(el)) continue;
        const text = cell.textContent?.replace(/\s+/g, ' ').trim();
        if (text) return text.slice(0, 40);
      }
    }
    
    // Check if element is in a list item
    const listItem = el.closest('li, [role="listitem"]');
    if (listItem) {
      // Get the main text identifier
      const name = listItem.querySelector('.name, .title, [class*="name"], [class*="title"], [class*="Name"]');
      if (name && !name.contains(el)) {
        const text = name.textContent?.trim();
        if (text) return text.slice(0, 40);
      }
    }
    
    // Check for data rows (common in dashboards)
    // More specific selectors to avoid matching unrelated elements like .arrow, .browser
    const dataRowSelectors = [
      '[class*="data-row"]',
      '[class*="DataRow"]',
      '[class*="list-item"]',
      '[class*="ListItem"]',
      '[class*="table-row"]',
      '[class*="TableRow"]',
      '.row[class*="item"]',
      '.Row[class*="Item"]',
    ];
    
    for (const selector of dataRowSelectors) {
      try {
        const dataRow = el.closest(selector);
        if (dataRow && !dataRow.matches('tr')) {
          const identifier = dataRow.querySelector('[class*="name"], [class*="title"], [class*="-id"]');
          if (identifier && !identifier.contains(el)) {
            const text = identifier.textContent?.trim();
            if (text && text.length < 40) return text;
          }
        }
      } catch {
        // Invalid selector, continue
      }
    }
    
    return null;
  }
  
  // -------------------------------------------------------------------------
  // Ref Generation
  // -------------------------------------------------------------------------
  
  private generateRef(role: string): string {
    const prefix = AccessibleSnapshotCapture.ROLE_PREFIXES[role] || 'E';
    this.refCounters[prefix] = (this.refCounters[prefix] || 0) + 1;
    return `${prefix}${this.refCounters[prefix]}`;
  }
  
  private renumberRefs(elements: AccessibleElement[]): void {
    // Reset counters
    const counters: Record<string, number> = {};
    
    for (const el of elements) {
      const prefix = AccessibleSnapshotCapture.ROLE_PREFIXES[el.role] || 'E';
      counters[prefix] = (counters[prefix] || 0) + 1;
      el.ref = `${prefix}${counters[prefix]}`;
    }
  }
  
  // -------------------------------------------------------------------------
  // Selector Generation
  // -------------------------------------------------------------------------
  
  private generateSelector(el: HTMLElement): string {
    el = this.proxyToggleActivationTargets.get(el) ?? el;
    try {
      // Try ID first
      if (el.id) {
        const selector = `#${CSS.escape(el.id)}`;
        // Verify it works
        if (document.querySelectorAll(selector).length === 1) {
          return selector;
        }
      }
      
      // Try data-testid
      const testId = el.getAttribute('data-testid');
      if (testId) {
        const selector = `[data-testid="${CSS.escape(testId)}"]`;
        if (document.querySelectorAll(selector).length === 1) {
          return selector;
        }
      }

      // ag-Grid (and similar virtualized data grids) anchor. These grids render
      // each column in BOTH a pinned and a center container and reuse identical
      // positional DOM structure across rows, so the nth-of-type fallback below
      // is ambiguous and often resolves to an invisible MIRROR copy (a 0x0
      // pinned/spacer cell) — the "element is not visible" click timeout we hit
      // opening a LeadRat lead. When the element lives in an ag-Grid cell, anchor
      // on the grid's own STABLE identifiers instead: the row's row-id (the record
      // id) and the cell's col-id, scoped to the specific container the element is
      // in so the selector resolves to exactly one visible node.
      const agSelector = this.generateAgGridSelector(el);
      if (agSelector) {
        return agSelector;
      }

      // Try unique class combination
      if (el.className && typeof el.className === 'string') {
        const classes = el.className.split(/\s+/).filter(c => c && !c.includes(':') && !c.startsWith('css-'));
        if (classes.length > 0) {
          const escapedClasses = classes.slice(0, 3).map(c => {
            try {
              return CSS.escape(c);
            } catch {
              return null;
            }
          }).filter(Boolean);
          
          if (escapedClasses.length > 0) {
            const selector = `${el.tagName.toLowerCase()}.${escapedClasses.join('.')}`;
            try {
              if (document.querySelectorAll(selector).length === 1) {
                return selector;
              }
            } catch {
              // Invalid selector, continue to fallback
            }
          }
        }
      }
      
      // Walk to a unique anchor or the body. A depth-limited suffix can resolve
      // several distinct fields and cause the server to fill the first one.
      // Building the complete ancestry avoids a document-wide uniqueness query
      // for every positional path while retaining each captured node's identity.
      const path: string[] = [];
      let current: HTMLElement | null = el;

      while (current && current !== document.body) {
        let selector = current.tagName.toLowerCase();

        if (current.id) {
          try {
            const idSelector = `#${CSS.escape(current.id)}`;
            if (document.querySelectorAll(idSelector).length === 1) {
              path.unshift(idSelector);
              break;
            }
          } catch {
            // Invalid ID, continue without it
          }
        }

        const ancestorTestId = current.getAttribute('data-testid');
        if (ancestorTestId) {
          try {
            const tidSelector = `[data-testid="${CSS.escape(ancestorTestId)}"]`;
            if (document.querySelectorAll(tidSelector).length === 1) {
              path.unshift(tidSelector);
              break;
            }
          } catch {
            // Invalid testid, continue without it
          }
        }

        const parent: HTMLElement | null = current.parentElement;
        if (parent) {
          const siblings = Array.from(parent.children).filter(
            child => child.tagName === current!.tagName
          );
          if (siblings.length > 1) {
            const index = siblings.indexOf(current) + 1;
            selector += `:nth-of-type(${index})`;
          }
        }

        path.unshift(selector);
        current = parent;
      }

      if (current === document.body) path.unshift('body');
      const pathSelector = path.join(' > ');
      if (pathSelector) {
        return pathSelector;
      }
      // no usable path built; drop to the catch-style basic fallback
      throw new Error('empty selector path');
    } catch (e) {
      // Fallback to basic tag + index
      const parent = el.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children);
        const index = siblings.indexOf(el);
        return `${el.tagName.toLowerCase()}:nth-child(${index + 1})`;
      }
      return el.tagName.toLowerCase();
    }
  }

  /**
   * Build a stable selector for an element inside an ag-Grid cell, anchored on
   * the grid's own identifiers (row `row-id` = the record id, cell `col-id`)
   * rather than a positional path. ag-Grid mirrors every column across pinned
   * and center containers and virtualizes rows, so nth-of-type paths are
   * ambiguous and frequently resolve to an invisible mirror copy. Scoping to the
   * container the element actually lives in yields exactly one (visible) match.
   * Returns null when the element isn't in an ag-Grid cell with usable ids.
   */
  private generateAgGridSelector(el: HTMLElement): string | null {
    try {
      const cell = el.closest('.ag-cell[col-id]') as HTMLElement | null;
      const row = el.closest('.ag-row[row-id]') as HTMLElement | null;
      if (!cell || !row) return null;

      const colId = cell.getAttribute('col-id');
      const rowId = row.getAttribute('row-id');
      if (!colId || !rowId) return null;

      // Attribute-value escaping (not CSS.escape, which is for identifiers): a
      // quoted attribute string only needs its backslashes and quotes escaped.
      const esc = (v: string) => v.replace(/["\\]/g, '\\$&');

      // Anchor to the specific pinned/center container so the same row-id/col-id
      // in the mirror container isn't also matched.
      const container = el.closest(
        '.ag-center-cols-container, .ag-pinned-left-cols-container, .ag-pinned-right-cols-container',
      ) as HTMLElement | null;
      const containerCls = container
        ? Array.from(container.classList).find(c => c.endsWith('-cols-container'))
        : null;
      const prefix = containerCls ? `.${containerCls} ` : '';

      const selector =
        `${prefix}.ag-row[row-id="${esc(rowId)}"] .ag-cell[col-id="${esc(colId)}"]`;

      // Verify: it must resolve, and the captured element must live inside the
      // matched cell (clicking the cell fires ag-Grid's delegated row handler).
      const match = document.querySelector(selector);
      if (match && (match === cell || match.contains(el))) {
        return selector;
      }
      return null;
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------
  
  private computeSnapshotHash(elements: AccessibleElement[], readableText = ''): string {
    // `context` is part of the identity, not decoration. On a page with N
    // identically-named controls — sixteen "Connect Now" buttons on LeadRat's
    // integrations grid — the owner label is the ONLY thing telling them apart
    // (see addOwnerContextForDuplicates). Hashing role:name:state alone makes a
    // re-ordered or re-filtered grid look unchanged, so the fresh snapshot is
    // never sent and the server keeps resolving refs against stale owners —
    // which is the click-the-wrong-card bug the owner labels exist to prevent.
    // Serialized as a structured tuple rather than `a:b:c` joined by '|'. Element
    // names and owner labels are arbitrary page text and routinely contain both
    // delimiters, and the flat form genuinely collides: {name:'a', context:'b:c'}
    // and {name:'a:b', context:'c'} both render "button:a:b:c:". A collision reads
    // as "no change", so the fresh snapshot is never sent. null (not '') for
    // absent fields also keeps a missing context distinct from an empty one.
    // Serialized as a structured tuple rather than `a:b:c` joined by '|'. Element
    // names and owner labels are arbitrary page text and routinely contain both
    // delimiters, and the flat form genuinely collides: {name:'a', context:'b:c'}
    // and {name:'a:b', context:'c'} both render "button:a:b:c:". A collision reads
    // as "no change", so the fresh snapshot is never sent. null (not '') for
    // absent fields also keeps a missing context distinct from an empty one.
    const key = JSON.stringify([
      elements.map(e => [e.role ?? null, e.name ?? null, e.context ?? null, e.state ?? null]),
      readableText,
    ]);
    return this.simpleHash(key);
  }
  
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
  
  private snapshotsAreEqual(a: AccessibleSnapshot, b: AccessibleSnapshot): boolean {
    if ((a.readableText ?? '') !== (b.readableText ?? '')) return false;
    if (a.elementCount !== b.elementCount) return false;
    if (a.elements.length !== b.elements.length) return false;
    
    for (let i = 0; i < a.elements.length; i++) {
      const elA = a.elements[i];
      const elB = b.elements[i];
      
      // Safety check for undefined elements
      if (!elA || !elB) return false;
      
      if (elA.name !== elB.name) return false;
      if (elA.role !== elB.role) return false;
      // Same reason as computeSnapshotHash: for duplicate-named controls the
      // owner label is the only distinguishing field, so ignoring it here
      // reports "unchanged" for exactly the change that matters most.
      if (elA.context !== elB.context) return false;

      // Also check state changes
      const stateA = JSON.stringify(elA.state || {});
      const stateB = JSON.stringify(elB.state || {});
      if (stateA !== stateB) return false;
    }
    
    return true;
  }
  
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ============================================================================
// Export singleton instance
// ============================================================================

export const accessibleSnapshot = new AccessibleSnapshotCapture();
